import React, { useState, useEffect, useRef } from "react";
import { Calculator, TrendingUp, DollarSign, Users, Clock, CheckCircle2, Sparkles, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Navigation from "../components/Navigation";
import Hero from "../components/Hero";
import BreakdownSection from "../components/BreakdownSection";
import { calculateResults } from "../utils/calculatorLogic";
import jsPDF from "jspdf";

const CalculatorPage = () => {
  const breakdownRef = useRef(null);
  
  // Loan Info (now first)
  const [loanAmount, setLoanAmount] = useState("");
  const [loanTermMonths, setLoanTermMonths] = useState("");
  const [loanInterestRate, setLoanInterestRate] = useState("");
  const [federalTaxRate, setFederalTaxRate] = useState("");
  const [inkindPaybackMonths, setInkindPaybackMonths] = useState("");
  
  // Restaurant Info State (customers and revenue per customer are mandatory)
  const [avgCustomersPerDay, setAvgCustomersPerDay] = useState("");
  const [avgRevenuePerCustomer, setAvgRevenuePerCustomer] = useState("");
  
  // Calculation trigger
  const [hasCalculated, setHasCalculated] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  
  // Margin Input Type - either detailed costs or direct net margin
  const [marginInputType, setMarginInputType] = useState("direct"); // "direct" or "detailed"
  const [directNetMargin, setDirectNetMargin] = useState(""); // For direct input (e.g., 7 for 7%)
  
  // Cost Structure (stored as percentages 0-100, converted for calculations)
  const [cogsType, setCogsType] = useState("");
  const [cogsPercentage, setCogsPercentage] = useState("");
  const [cogsAmount, setCogsAmount] = useState("");
  
  const [laborType, setLaborType] = useState("");
  const [laborPercentage, setLaborPercentage] = useState("");
  const [laborAmount, setLaborAmount] = useState("");
  
  const [overheadType, setOverheadType] = useState("");
  const [overheadPercentage, setOverheadPercentage] = useState("");
  const [overheadAmount, setOverheadAmount] = useState("");
  
  // Results
  const [results, setResults] = useState(null);
  const [negativeMarginError, setNegativeMarginError] = useState(false);

  // Format money with commas
  const formatMoney = (value) => {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Check if all required fields are filled
  const canCalculate = () => {
    const baseRequired = loanAmount && loanTermMonths && loanInterestRate && 
           inkindPaybackMonths && avgCustomersPerDay && avgRevenuePerCustomer;
    
    if (marginInputType === "direct") {
      // Direct net margin already accounts for taxes
      return baseRequired && directNetMargin;
    } else {
      // Detailed costs need tax rate to calculate net margin
      return baseRequired && federalTaxRate && cogsType && laborType && overheadType &&
             (cogsType === "Percentage of Revenue" ? cogsPercentage : cogsAmount) &&
             (laborType === "Percentage of Revenue" ? laborPercentage : laborAmount) &&
             (overheadType === "Percentage of Revenue" ? overheadPercentage : overheadAmount);
    }
  };

  // Calculate results when button is clicked
  const handleCalculate = () => {
    if (!canCalculate()) return;
    
    const avgPartySize = 3;
    const monthlyRevenue = Number(avgCustomersPerDay) * Number(avgRevenuePerCustomer) * 30;
    
    let netMarginDecimal;
    
    if (marginInputType === "direct") {
      // Direct net margin input
      netMarginDecimal = Number(directNetMargin) / 100;
      
      // Check for invalid margin
      if (netMarginDecimal <= 0 || netMarginDecimal >= 1) {
        setErrorDetails({
          monthlyRevenue,
          netMargin: Number(directNetMargin),
          message: "Net margin must be between 0% and 100%"
        });
        setNegativeMarginError(true);
        setResults(null);
        setHasCalculated(true);
        return;
      }
    } else {
      // Calculate costs for detailed error
      const cogsCost = cogsType === "Percentage of Revenue" 
        ? (Number(cogsPercentage) / 100) * monthlyRevenue 
        : Number(cogsAmount);
      const laborCost = laborType === "Percentage of Revenue" 
        ? (Number(laborPercentage) / 100) * monthlyRevenue 
        : Number(laborAmount);
      const overheadCost = overheadType === "Percentage of Revenue" 
        ? (Number(overheadPercentage) / 100) * monthlyRevenue 
        : Number(overheadAmount);
      const totalCosts = cogsCost + laborCost + overheadCost;
      
      // Check for negative margin BEFORE calculating
      if (totalCosts >= monthlyRevenue) {
        setErrorDetails({
          monthlyRevenue,
          cogsCost,
          laborCost,
          overheadCost,
          totalCosts,
          deficit: totalCosts - monthlyRevenue
        });
        setNegativeMarginError(true);
        setResults(null);
        setHasCalculated(true);
        return;
      }
      
      // Calculate net margin from costs - apply tax rate to get NET margin
      const grossMargin = 1 - (totalCosts / monthlyRevenue);
      const taxRate = Number(federalTaxRate) / 100;
      netMarginDecimal = grossMargin * (1 - taxRate);
    }
    
    const calculatedResults = calculateResults({
      revenueType: "Monthly Revenue Estimator",
      specifiedRevenue: monthlyRevenue,
      avgCustomersPerDay: Number(avgCustomersPerDay),
      avgRevenuePerCustomer: Number(avgRevenuePerCustomer),
      avgPartySize,
      marginInputType,
      directNetMargin: netMarginDecimal,
      cogsType,
      cogsPercentage: (Number(cogsPercentage) || 30) / 100,
      cogsAmount: Number(cogsAmount) || 30000,
      laborType,
      laborPercentage: (Number(laborPercentage) || 30) / 100,
      laborAmount: Number(laborAmount) || 20000,
      overheadType,
      overheadPercentage: (Number(overheadPercentage) || 12) / 100,
      overheadAmount: Number(overheadAmount) || 12000,
      loanAmount: Number(loanAmount),
      loanTermMonths: Number(loanTermMonths),
      loanInterestRate: Number(loanInterestRate) / 100,
      inkindCreditAmount: Number(loanAmount),
      inkindPaybackMonths: Number(inkindPaybackMonths)
    });

    setNegativeMarginError(false);
    setErrorDetails(null);
    setResults(calculatedResults);
    setHasCalculated(true);
  };

  // Reset calculation when inputs change
  useEffect(() => {
    if (hasCalculated) {
      setResults(null);
      setNegativeMarginError(false);
      setErrorDetails(null);
      setHasCalculated(false);
    }
  }, [
    avgCustomersPerDay, avgRevenuePerCustomer,
    cogsType, cogsPercentage, cogsAmount, laborType, laborPercentage, laborAmount,
    overheadType, overheadPercentage, overheadAmount, loanAmount, loanTermMonths,
    loanInterestRate, federalTaxRate, inkindPaybackMonths, marginInputType, directNetMargin
  ]);

  const loadSampleData = () => {
    setLoanAmount(100000);
    setLoanTermMonths(60);
    setLoanInterestRate(10);
    setFederalTaxRate(30);
    setInkindPaybackMonths(36);
    setAvgCustomersPerDay(100);
    setAvgRevenuePerCustomer(30);
    setMarginInputType("detailed");
    setCogsType("Percentage of Revenue");
    setCogsPercentage(30);
    setLaborType("Percentage of Revenue");
    setLaborPercentage(25);
    setOverheadType("Flat Amount");
    setOverheadAmount(15000);
    // Reset calculation state
    setResults(null);
    setNegativeMarginError(false);
    setErrorDetails(null);
    setHasCalculated(false);
  };

  // Quick Service Restaurant (Fast Food / Fast Casual)
  // Realistic: ~300-500 customers/day, $12-18 avg check, 7-9% net margin
  const loadQuickService = () => {
    setLoanAmount(75000);
    setLoanTermMonths(48);
    setLoanInterestRate(9);
    setFederalTaxRate(30);
    setInkindPaybackMonths(24);
    setAvgCustomersPerDay(350);
    setAvgRevenuePerCustomer(14);
    setMarginInputType("direct");
    setDirectNetMargin(8); // 8% net margin typical for QSR (already accounts for taxes)
    // Clear detailed inputs
    setCogsType("");
    setCogsPercentage("");
    setLaborType("");
    setLaborPercentage("");
    setOverheadType("");
    setOverheadAmount("");
    // Reset calculation state
    setResults(null);
    setNegativeMarginError(false);
    setErrorDetails(null);
    setHasCalculated(false);
  };

  // Casual Dining Restaurant
  // Realistic: ~150-250 customers/day, $28-45 avg check, 5-7% net margin
  const loadCasualDining = () => {
    setLoanAmount(150000);
    setLoanTermMonths(60);
    setLoanInterestRate(10);
    setFederalTaxRate(32);
    setInkindPaybackMonths(36);
    setAvgCustomersPerDay(180);
    setAvgRevenuePerCustomer(38);
    setMarginInputType("direct");
    setDirectNetMargin(6); // 6% net margin typical for casual dining
    // Clear detailed inputs
    setCogsType("");
    setCogsPercentage("");
    setLaborType("");
    setLaborPercentage("");
    setOverheadType("");
    setOverheadAmount("");
    // Reset calculation state
    setResults(null);
    setNegativeMarginError(false);
    setErrorDetails(null);
    setHasCalculated(false);
  };

  // Fine Dining Restaurant
  // Realistic: ~60-100 customers/day, $95-150 avg check, 3-5% net margin
  const loadFineDining = () => {
    setLoanAmount(250000);
    setLoanTermMonths(72);
    setLoanInterestRate(8);
    setFederalTaxRate(35);
    setInkindPaybackMonths(48);
    setAvgCustomersPerDay(75);
    setAvgRevenuePerCustomer(120);
    setMarginInputType("direct");
    setDirectNetMargin(4); // 4% net margin typical for fine dining
    // Clear detailed inputs
    setCogsType("");
    setCogsPercentage("");
    setLaborType("");
    setLaborPercentage("");
    setOverheadType("");
    setOverheadAmount("");
    // Reset calculation state
    setResults(null);
    setNegativeMarginError(false);
    setErrorDetails(null);
    setHasCalculated(false);
  };

  const downloadPDF = async () => {
    if (!results) return;
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let y = 20;
      
      // Helper functions
      const formatCurrency = (value) => {
        return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };
      
      // For values already in decimal form (0.35 for 35%)
      const formatPercentDecimal = (value) => {
        return (value * 100).toFixed(1) + '%';
      };
      
      // For values already as whole numbers (35 for 35%)
      const formatPercentWhole = (value) => {
        return Number(value).toFixed(0) + '%';
      };
      
      const addText = (text, x, currentY, options = {}) => {
        const { fontSize = 10, fontStyle = 'normal', color = [0, 0, 0], align = 'left', maxWidth = null } = options;
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        pdf.setTextColor(...color);
        
        if (maxWidth) {
          const lines = pdf.splitTextToSize(String(text), maxWidth);
          pdf.text(lines, x, currentY, { align });
          return currentY + (lines.length * fontSize * 0.4);
        }
        
        pdf.text(String(text), x, currentY, { align });
        return currentY + (fontSize * 0.4);
      };
      
      const addLine = (currentY) => {
        pdf.setDrawColor(180, 180, 180);
        pdf.setLineWidth(0.3);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        return currentY + 4;
      };
      
      const addThickLine = (currentY) => {
        pdf.setDrawColor(197, 165, 114);
        pdf.setLineWidth(0.8);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        return currentY + 5;
      };
      
      const checkNewPage = (currentY, neededSpace = 25) => {
        if (currentY > 275 - neededSpace) {
          pdf.addPage();
          return 20;
        }
        return currentY;
      };
      
      const addSectionTitle = (title, currentY) => {
        currentY = checkNewPage(currentY, 15);
        pdf.setDrawColor(197, 165, 114);
        pdf.setLineWidth(1);
        pdf.line(margin, currentY, margin + 5, currentY);
        addText(title, margin + 8, currentY + 1, { fontSize: 12, fontStyle: 'bold', color: [80, 60, 30] });
        pdf.line(margin + 8 + pdf.getTextWidth(title) + 3, currentY, pageWidth - margin, currentY);
        return currentY + 8;
      };
      
      const addSubTitle = (title, currentY) => {
        currentY = checkNewPage(currentY, 12);
        addText(title, margin, currentY, { fontSize: 10, fontStyle: 'bold', color: [60, 60, 60] });
        return currentY + 6;
      };
      
      const addBulletPoint = (text, currentY, indent = 0) => {
        currentY = checkNewPage(currentY, 8);
        addText('•', margin + indent, currentY, { fontSize: 10 });
        return addText(text, margin + 4 + indent, currentY, { fontSize: 9, maxWidth: contentWidth - 8 - indent });
      };
      
      const addKeyValue = (label, value, currentY, options = {}) => {
        currentY = checkNewPage(currentY, 8);
        const { labelWidth = 100, bold = false } = options;
        addText(label, margin, currentY, { fontSize: 9, color: [80, 80, 80] });
        addText(value, pageWidth - margin, currentY, { fontSize: 9, fontStyle: bold ? 'bold' : 'normal', align: 'right' });
        return currentY + 5;
      };
      
      const addCalculationRow = (description, formula, result, currentY) => {
        currentY = checkNewPage(currentY, 16);
        // Light gray background for calculation
        pdf.setFillColor(250, 250, 250);
        pdf.rect(margin, currentY - 2, contentWidth, 13, 'F');
        pdf.setDrawColor(220, 220, 220);
        pdf.rect(margin, currentY - 2, contentWidth, 13, 'S');
        
        addText(description, margin + 2, currentY + 2, { fontSize: 8, color: [100, 100, 100] });
        addText(formula, margin + 2, currentY + 7, { fontSize: 8 });
        addText(result, pageWidth - margin - 2, currentY + 5, { fontSize: 10, fontStyle: 'bold', color: [0, 100, 50], align: 'right' });
        return currentY + 16;
      };
      
      // Pre-calculate values (no tax calculations)
      const extraMonths = loanTermMonths - inkindPaybackMonths;
      const monthlyProfit = results.inkind.additionalMonthlyNetProfit;
      const totalProfitOverTerm = monthlyProfit * inkindPaybackMonths;
      const extraSavings = extraMonths * results.loan.monthlyPayment;
      const totalCombined = totalProfitOverTerm + extraSavings;
      const customerDiff = results.loan.customersNeededPerDay - results.inkind.customersPerDay;
      const dailyRevenue = avgCustomersPerDay * avgRevenuePerCustomer;
      const monthlyRevenue = results.restaurant.monthlyRevenue;
      const netMargin = results.restaurant.netMargin;
      const totalInterest = results.loan.totalPaidBack - loanAmount;
      
      // ===================== PAGE 1: EXECUTIVE SUMMARY =====================
      
      // Simple header with border
      pdf.setDrawColor(197, 165, 114);
      pdf.setLineWidth(2);
      pdf.line(margin, 15, pageWidth - margin, 15);
      addText('inKind Capital Savings Analysis', margin, 25, { fontSize: 18, fontStyle: 'bold' });
      addText('Comprehensive Comparison Report', margin, 32, { fontSize: 10, color: [100, 100, 100] });
      addText(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageWidth - margin, 32, { fontSize: 9, color: [100, 100, 100], align: 'right' });
      pdf.line(margin, 38, pageWidth - margin, 38);
      
      y = 50;
      
      // Executive Summary Box (border only, no fill)
      pdf.setDrawColor(197, 165, 114);
      pdf.setLineWidth(1);
      pdf.rect(margin, y - 3, contentWidth, 40, 'S');
      
      addText('YOUR TOTAL SAVINGS WITH INKIND', margin + 5, y + 5, { fontSize: 10, fontStyle: 'bold', color: [150, 120, 60] });
      addText(formatCurrency(totalCombined), margin + 5, y + 20, { fontSize: 24, fontStyle: 'bold' });
      addText(`Over the full ${loanTermMonths}-month comparison period`, margin + 5, y + 28, { fontSize: 9, color: [100, 100, 100] });
      
      // Breakdown on right side
      addText('Breakdown:', margin + 110, y + 5, { fontSize: 8, fontStyle: 'bold', color: [100, 100, 100] });
      addText(`Retained Profit: ${formatCurrency(totalProfitOverTerm)}`, margin + 110, y + 12, { fontSize: 8 });
      addText(`Avoided Payments: ${formatCurrency(extraSavings)}`, margin + 110, y + 18, { fontSize: 8 });
      
      y += 48;
      
      // Quick Stats (simple boxes with borders)
      const boxW = (contentWidth - 10) / 3;
      const statsData = [
        { label: 'Monthly Profit Retained', value: formatCurrency(monthlyProfit) },
        { label: 'Yearly Profit Retained', value: formatCurrency(monthlyProfit * 12) },
        { label: 'Fewer Customers/Day', value: customerDiff.toFixed(1) }
      ];
      
      statsData.forEach((stat, i) => {
        const boxX = margin + (i * (boxW + 5));
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.rect(boxX, y, boxW, 18, 'S');
        addText(stat.label, boxX + boxW/2, y + 6, { fontSize: 8, color: [100, 100, 100], align: 'center' });
        addText(stat.value, boxX + boxW/2, y + 13, { fontSize: 11, fontStyle: 'bold', align: 'center' });
      });
      
      y += 28;
      y = addThickLine(y);
      
      // ===================== RESTAURANT DETAILS =====================
      y = addSectionTitle('YOUR RESTAURANT PROFILE', y);
      
      y = addSubTitle('Revenue Information', y);
      y = addKeyValue('Average Customers Per Day', String(avgCustomersPerDay), y);
      y = addKeyValue('Average Revenue Per Customer', formatCurrency(avgRevenuePerCustomer), y);
      y = addKeyValue('Estimated Daily Revenue', formatCurrency(dailyRevenue), y);
      y = addKeyValue('Estimated Monthly Revenue', formatCurrency(monthlyRevenue), y, { bold: true });
      y += 3;
      
      // Cost Structure section - only show when using detailed cost breakdown
      if (marginInputType === "detailed") {
        y = addSubTitle('Cost Structure', y);
        // cogsPercentage/laborPercentage are stored as whole numbers (28 for 28%), need to divide by 100
        const cogsTotal = cogsType === "Percentage of Revenue" ? ((cogsPercentage / 100) * monthlyRevenue) : Number(cogsAmount);
        const laborTotal = laborType === "Percentage of Revenue" ? ((laborPercentage / 100) * monthlyRevenue) : Number(laborAmount);
        const overheadTotal = overheadType === "Percentage of Revenue" ? ((overheadPercentage / 100) * monthlyRevenue) : Number(overheadAmount);
        const totalCosts = cogsTotal + laborTotal + overheadTotal;
        
        y = addKeyValue('Cost of Goods Sold (COGS)', formatCurrency(cogsTotal) + '/month', y);
        y = addKeyValue('Labor Costs', formatCurrency(laborTotal) + '/month', y);
        y = addKeyValue('Overhead & Operating', formatCurrency(overheadTotal) + '/month', y);
        y = addKeyValue('Total Monthly Costs', formatCurrency(totalCosts), y, { bold: true });
        y += 3;
        // Show Profit Margin section for detailed mode
        y = addSubTitle('Profit Margin', y);
        y = addKeyValue('Net Profit Margin', formatPercentDecimal(netMargin), y, { bold: true });
        y += 3;
      } else {
        // Direct margin input mode - show a note about input method
        y = addSubTitle('Profit Margin (Direct Input)', y);
        y = addKeyValue('Input Method', 'Direct Net Margin Entry', y);
        y = addKeyValue('Net Profit Margin', formatPercentDecimal(netMargin), y, { bold: true });
        y += 3;
      }
      
      // Explanation box
      pdf.setDrawColor(200, 180, 100);
      pdf.setLineWidth(0.5);
      pdf.rect(margin, y, contentWidth, 14, 'S');
      addText('Why Net Margin Matters:', margin + 3, y + 5, { fontSize: 8, fontStyle: 'bold', color: [150, 120, 50] });
      addText('Your net margin represents how much profit you keep per dollar of revenue. Bank loans must be repaid from profit.', margin + 3, y + 10, { fontSize: 7, color: [80, 80, 80] });
      y += 18;
      
      // ===================== PAGE 2: FINANCING COMPARISON =====================
      pdf.addPage();
      y = 20;
      
      y = addSectionTitle('DETAILED FINANCING COMPARISON', y);
      
      // Create proper two-column table
      const leftCol = margin;
      const midPoint = pageWidth / 2;
      const rightCol = midPoint + 5;
      const labelColWidth = 55;
      
      // Table header
      pdf.setFillColor(245, 245, 245);
      pdf.rect(leftCol, y, contentWidth, 10, 'F');
      pdf.setDrawColor(180, 180, 180);
      pdf.rect(leftCol, y, contentWidth, 10, 'S');
      
      addText('', leftCol + 2, y + 7, { fontSize: 9, fontStyle: 'bold' });
      addText('Bank Loan', leftCol + labelColWidth + 15, y + 7, { fontSize: 9, fontStyle: 'bold', align: 'center' });
      addText('inKind Capital', rightCol + 30, y + 7, { fontSize: 9, fontStyle: 'bold', align: 'center' });
      y += 12;
      
      // Table rows
      const tableRows = [
        { label: 'Amount Received', loan: formatCurrency(loanAmount), inkind: formatCurrency(loanAmount) },
        { label: 'Credit Value', loan: 'N/A', inkind: formatCurrency(loanAmount * 2) + ' (2x)' },
        { label: 'Term Length', loan: loanTermMonths + ' months', inkind: inkindPaybackMonths + ' months' },
        { label: 'Interest Rate', loan: formatPercentWhole(loanInterestRate) + ' APR', inkind: '0% (Interest-Free)' },
        { label: 'Total Interest Paid', loan: formatCurrency(totalInterest), inkind: '$0.00' },
        { label: 'Total Repayment', loan: formatCurrency(results.loan.totalPaidBack), inkind: formatCurrency(loanAmount * 2) + ' (credits)' },
        { label: 'Monthly Payment / Redemption', loan: formatCurrency(results.loan.monthlyPayment), inkind: formatCurrency(results.inkind.amountPerMonth) + ' (credit)' },
        { label: 'Customers/Day Needed', loan: results.loan.customersNeededPerDay.toFixed(1), inkind: results.inkind.customersPerDay.toFixed(1) },
      ];
      
      tableRows.forEach((row, i) => {
        // Alternating row background
        if (i % 2 === 0) {
          pdf.setFillColor(252, 252, 252);
          pdf.rect(leftCol, y - 1, contentWidth, 8, 'F');
        }
        pdf.setDrawColor(230, 230, 230);
        pdf.line(leftCol, y + 6, pageWidth - margin, y + 6);
        
        addText(row.label, leftCol + 2, y + 4, { fontSize: 8, color: [80, 80, 80] });
        addText(row.loan, leftCol + labelColWidth + 35, y + 4, { fontSize: 8, align: 'right' });
        addText(row.inkind, pageWidth - margin - 2, y + 4, { fontSize: 8, fontStyle: 'bold', color: [0, 100, 50], align: 'right' });
        y += 8;
      });
      
      y += 8;
      
      // Key insight box
      pdf.setDrawColor(0, 150, 0);
      pdf.setLineWidth(0.5);
      pdf.rect(margin, y, contentWidth, 20, 'S');
      addText('KEY INSIGHT: Why inKind Needs Fewer Customers', margin + 3, y + 6, { fontSize: 9, fontStyle: 'bold', color: [0, 100, 0] });
      addText(`Bank loans require NET profit (${formatPercentDecimal(netMargin)}) to repay. inKind counts full customer revenue.`, margin + 3, y + 12, { fontSize: 8 });
      addText(`Each customer: ${formatCurrency(avgRevenuePerCustomer)} toward inKind vs ${formatCurrency(avgRevenuePerCustomer * netMargin)} toward bank loan.`, margin + 3, y + 17, { fontSize: 8 });
      y += 28;
      
      // ===================== CUSTOMER MATHEMATICS =====================
      y = addSectionTitle('THE CUSTOMER MATHEMATICS', y);
      
      y = addSubTitle('Bank Loan: Based on PROFIT', y);
      y = addCalculationRow(
        'Profit per customer',
        `${formatCurrency(avgRevenuePerCustomer)} x ${formatPercentDecimal(netMargin)} net margin`,
        formatCurrency(avgRevenuePerCustomer * netMargin)
      , y);
      y = addCalculationRow(
        'Daily loan cost',
        `${formatCurrency(results.loan.monthlyPayment)}/mo x 12 / 365`,
        formatCurrency(results.loan.dailyCost) + '/day'
      , y);
      y = addCalculationRow(
        'Customers needed/day',
        `${formatCurrency(results.loan.dailyCost)} / ${formatCurrency(avgRevenuePerCustomer * netMargin)}`,
        results.loan.customersNeededPerDay.toFixed(1) + ' customers'
      , y);
      
      y += 3;
      y = addSubTitle('inKind: Based on REVENUE', y);
      y = addCalculationRow(
        'Daily redemption',
        `${formatCurrency(results.inkind.amountPerMonth)}/mo x 12 / 365`,
        formatCurrency(results.inkind.amountPerDay) + '/day'
      , y);
      y = addCalculationRow(
        'Customers needed/day',
        `${formatCurrency(results.inkind.amountPerDay)} / ${formatCurrency(avgRevenuePerCustomer)}`,
        results.inkind.customersPerDay.toFixed(1) + ' customers'
      , y);
      
      // Result highlight - BIG and GREEN
      y += 5;
      pdf.setFillColor(230, 255, 230);
      pdf.setDrawColor(0, 150, 0);
      pdf.setLineWidth(2);
      pdf.rect(margin, y, contentWidth, 28, 'FD');
      addText('RESULT', margin + contentWidth/2, y + 8, { fontSize: 10, fontStyle: 'bold', color: [0, 100, 0], align: 'center' });
      addText(`${customerDiff.toFixed(1)} FEWER CUSTOMERS PER DAY`, margin + contentWidth/2, y + 18, { fontSize: 14, fontStyle: 'bold', color: [0, 120, 0], align: 'center' });
      addText(`with inKind (${results.loan.customersNeededPerDay.toFixed(1)} vs ${results.inkind.customersPerDay.toFixed(1)})`, margin + contentWidth/2, y + 25, { fontSize: 9, color: [0, 80, 0], align: 'center' });
      y += 34;
      
      // ===================== PAGE 3: PROFIT & TAX =====================
      pdf.addPage();
      y = 20;
      
      y = addSectionTitle('PROFIT RETENTION ANALYSIS', y);
      
      addText('Since inKind needs fewer customers, the "extra" customers generate profit that stays with you.', margin, y, { fontSize: 9 });
      y += 8;
      
      y = addCalculationRow(
        'Customer difference',
        `${results.loan.customersNeededPerDay.toFixed(1)} vs ${results.inkind.customersPerDay.toFixed(1)}`,
        customerDiff.toFixed(1) + ' customers/day'
      , y);
      y = addCalculationRow(
        'Revenue from extra customers',
        `${customerDiff.toFixed(1)} x ${formatCurrency(avgRevenuePerCustomer)}`,
        formatCurrency(results.inkind.additionalDailyRevenue) + '/day'
      , y);
      y = addCalculationRow(
        'NET profit retained (after tax)',
        `${formatCurrency(results.inkind.additionalDailyRevenue)} x ${formatPercentDecimal(netMargin)}`,
        formatCurrency(results.inkind.additionalDailyNetProfit) + '/day'
      , y);
      
      // Profit summary table
      y += 5;
      pdf.setDrawColor(0, 150, 0);
      pdf.rect(margin, y, contentWidth, 28, 'S');
      addText('PROFIT RETAINED SUMMARY', margin + 3, y + 6, { fontSize: 9, fontStyle: 'bold', color: [0, 100, 0] });
      addText('Daily: ' + formatCurrency(results.inkind.additionalDailyNetProfit), margin + 5, y + 13, { fontSize: 8 });
      addText('Monthly: ' + formatCurrency(monthlyProfit), margin + 5, y + 19, { fontSize: 8 });
      addText('Yearly: ' + formatCurrency(monthlyProfit * 12), margin + 5, y + 25, { fontSize: 8 });
      addText(`Over ${inkindPaybackMonths} months: ` + formatCurrency(monthlyProfit * inkindPaybackMonths), margin + 80, y + 19, { fontSize: 10, fontStyle: 'bold' });
      y += 40;
      
      // ===================== PAGE 4: EXTRA MONTHS SAVINGS =====================
      pdf.addPage();
      y = 20;
      
      y = addSectionTitle('EXTRA MONTHS OF SAVINGS', y);
      
      addText(`inKind pays off in ${inkindPaybackMonths} months vs ${loanTermMonths} months for a bank loan = ${extraMonths} extra months payment-free!`, margin, y, { fontSize: 9 });
      y += 8;
      
      y = addCalculationRow(
        'Extra payment-free months',
        `${loanTermMonths} - ${inkindPaybackMonths}`,
        extraMonths + ' months'
      , y);
      y = addCalculationRow(
        'Monthly payment avoided',
        'Bank loan payment',
        formatCurrency(results.loan.monthlyPayment) + '/month'
      , y);
      
      // PROMINENT Total Avoided Payments box
      y += 5;
      pdf.setFillColor(240, 255, 240);
      pdf.setDrawColor(0, 150, 0);
      pdf.setLineWidth(2);
      pdf.rect(margin, y, contentWidth, 28, 'FD');
      addText('TOTAL AVOIDED PAYMENTS', margin + contentWidth/2, y + 7, { fontSize: 11, fontStyle: 'bold', color: [0, 100, 0], align: 'center' });
      addText(formatCurrency(extraSavings), margin + contentWidth/2, y + 18, { fontSize: 16, fontStyle: 'bold', color: [0, 120, 0], align: 'center' });
      addText(`(${extraMonths} months x ${formatCurrency(results.loan.monthlyPayment)}/mo you DON'T have to pay!)`, margin + contentWidth/2, y + 25, { fontSize: 8, color: [0, 80, 0], align: 'center' });
      y += 35;
      
      // ===================== GRAND TOTAL =====================
      y = addSectionTitle('COMPLETE SAVINGS BREAKDOWN', y);
      
      // Component boxes - only 2 components now (no tax)
      y = addKeyValue('1. Retained Profit (' + inkindPaybackMonths + ' months)', formatCurrency(monthlyProfit * inkindPaybackMonths), y, { bold: true });
      addText(`   ${customerDiff.toFixed(1)} fewer customers x ${formatCurrency(avgRevenuePerCustomer * netMargin)} profit x 30 days x ${inkindPaybackMonths} mo`, margin, y, { fontSize: 7, color: [100, 100, 100] });
      y += 8;
      
      y = addKeyValue('2. Avoided Payments (' + extraMonths + ' months)', formatCurrency(extraSavings), y, { bold: true });
      addText(`   ${extraMonths} mo x ${formatCurrency(results.loan.monthlyPayment)}/mo`, margin, y, { fontSize: 7, color: [100, 100, 100] });
      y += 10;
      
      // Grand total box
      pdf.setDrawColor(197, 165, 114);
      pdf.setLineWidth(2);
      pdf.rect(margin, y, contentWidth, 25, 'S');
      addText('TOTAL COMBINED SAVINGS', margin + contentWidth/2, y + 8, { fontSize: 12, fontStyle: 'bold', align: 'center' });
      addText(formatCurrency(totalCombined), margin + contentWidth/2, y + 19, { fontSize: 20, fontStyle: 'bold', align: 'center' });
      y += 32;
      
      // PROMINENT Bottom Line box
      pdf.setFillColor(250, 248, 240);
      pdf.setDrawColor(197, 165, 114);
      pdf.setLineWidth(2);
      pdf.rect(margin, y, contentWidth, 30, 'FD');
      addText('THE BOTTOM LINE', margin + contentWidth/2, y + 8, { fontSize: 12, fontStyle: 'bold', color: [150, 120, 60], align: 'center' });
      addText(`By choosing inKind, you keep ${formatCurrency(monthlyProfit * 12)} more per year!`, margin + contentWidth/2, y + 18, { fontSize: 11, fontStyle: 'bold', align: 'center' });
      addText(`That's ${formatCurrency(monthlyProfit)} extra in your pocket every single month.`, margin + contentWidth/2, y + 26, { fontSize: 9, color: [80, 80, 80], align: 'center' });
      y += 38;
      
      // ===================== PAGE 5: 12-MONTH BUSINESS CYCLE =====================
      pdf.addPage();
      y = 20;
      
      y = addSectionTitle('12-MONTH BUSINESS CYCLE ANALYSIS', y);
      
      addText('See how each financing option impacts your NET PROFIT through seasonal ups and downs.', margin, y, { fontSize: 9 });
      y += 8;
      
      // Daily Operational Burden section
      y = addSubTitle('Daily Operational Burden', y);
      addText('Fewer customers to service debt = less operational pressure, less stress, more sustainable operations', margin, y, { fontSize: 8, color: [150, 120, 60] });
      y += 6;
      
      const dailyNetProfit = Number(avgCustomersPerDay) * avgRevenuePerCustomer * netMargin;
      const dailyLoanCost = results.loan.monthlyPayment / 30;
      const loanPercentOfDailyProfit = (dailyLoanCost / dailyNetProfit) * 100;
      const inkindCustomersPerDay = results.inkind.customersPerDay;
      const inkindDailyProfitCost = inkindCustomersPerDay * (avgRevenuePerCustomer * netMargin);
      const inkindPercentOfDailyProfit = (inkindDailyProfitCost / dailyNetProfit) * 100;
      
      // Two-column comparison boxes
      const boxWidth = (contentWidth - 10) / 2;
      
      // Bank Loan box
      pdf.setFillColor(255, 240, 240);
      pdf.setDrawColor(200, 100, 100);
      pdf.setLineWidth(1);
      pdf.rect(margin, y, boxWidth, 35, 'FD');
      addText('BANK LOAN', margin + boxWidth/2, y + 6, { fontSize: 9, fontStyle: 'bold', color: [180, 80, 80], align: 'center' });
      addText(loanPercentOfDailyProfit.toFixed(1) + '%', margin + boxWidth/2, y + 18, { fontSize: 18, fontStyle: 'bold', align: 'center' });
      addText('of daily net profit', margin + boxWidth/2, y + 25, { fontSize: 7, color: [100, 100, 100], align: 'center' });
      addText(formatCurrency(dailyLoanCost) + '/day payment', margin + boxWidth/2, y + 31, { fontSize: 7, color: [150, 80, 80], align: 'center' });
      
      // inKind box
      pdf.setFillColor(255, 250, 240);
      pdf.setDrawColor(197, 165, 114);
      pdf.rect(margin + boxWidth + 10, y, boxWidth, 35, 'FD');
      addText('INKIND', margin + boxWidth + 10 + boxWidth/2, y + 6, { fontSize: 9, fontStyle: 'bold', color: [150, 120, 60], align: 'center' });
      addText(inkindPercentOfDailyProfit.toFixed(1) + '%', margin + boxWidth + 10 + boxWidth/2, y + 18, { fontSize: 18, fontStyle: 'bold', align: 'center' });
      addText('of daily net profit', margin + boxWidth + 10 + boxWidth/2, y + 25, { fontSize: 7, color: [100, 100, 100], align: 'center' });
      addText(formatCurrency(inkindDailyProfitCost) + ' from ' + inkindCustomersPerDay.toFixed(1) + ' customers', margin + boxWidth + 10 + boxWidth/2, y + 31, { fontSize: 7, color: [150, 120, 60], align: 'center' });
      y += 42;
      
      // Operational Burden Highlight Box
      const dailyCustomerDiff = results.loan.customersNeededPerDay - inkindCustomersPerDay;
      pdf.setFillColor(240, 255, 240);
      pdf.setDrawColor(0, 150, 0);
      pdf.setLineWidth(1.5);
      pdf.rect(margin, y, contentWidth, 32, 'FD');
      addText('REDUCED OPERATIONAL PRESSURE WITH INKIND', margin + contentWidth/2, y + 7, { fontSize: 9, fontStyle: 'bold', color: [0, 100, 0], align: 'center' });
      
      // Three columns of stats
      const statWidth = contentWidth / 3;
      addText(dailyCustomerDiff.toFixed(1), margin + statWidth/2, y + 17, { fontSize: 14, fontStyle: 'bold', align: 'center' });
      addText('fewer customers/day', margin + statWidth/2, y + 23, { fontSize: 7, color: [80, 80, 80], align: 'center' });
      
      addText((dailyCustomerDiff * 30).toFixed(0), margin + statWidth + statWidth/2, y + 17, { fontSize: 14, fontStyle: 'bold', align: 'center' });
      addText('fewer customers/month', margin + statWidth + statWidth/2, y + 23, { fontSize: 7, color: [80, 80, 80], align: 'center' });
      
      addText((dailyCustomerDiff * 365).toFixed(0), margin + statWidth * 2 + statWidth/2, y + 17, { fontSize: 14, fontStyle: 'bold', align: 'center' });
      addText('fewer customers/year', margin + statWidth * 2 + statWidth/2, y + 23, { fontSize: 7, color: [80, 80, 80], align: 'center' });
      
      addText('Less pressure on your kitchen, staff, and operations. More time for quality service.', margin + contentWidth/2, y + 29, { fontSize: 7, color: [0, 80, 0], align: 'center' });
      y += 38;
      
      // Seasonal multipliers for 12-month analysis
      const seasonalData = [
        { month: 'Jan', mult: 0.75, label: 'Slow' },
        { month: 'Feb', mult: 0.80, label: 'Slow' },
        { month: 'Mar', mult: 0.95, label: 'Avg' },
        { month: 'Apr', mult: 1.05, label: 'Avg' },
        { month: 'May', mult: 1.15, label: 'Busy' },
        { month: 'Jun', mult: 1.20, label: 'Busy' },
        { month: 'Jul', mult: 1.25, label: 'Peak' },
        { month: 'Aug', mult: 1.20, label: 'Busy' },
        { month: 'Sep', mult: 1.00, label: 'Avg' },
        { month: 'Oct', mult: 1.05, label: 'Avg' },
        { month: 'Nov', mult: 1.10, label: 'Busy' },
        { month: 'Dec', mult: 1.30, label: 'Peak' },
      ];
      
      const baseMonthlyNetProfit = monthlyRevenue * netMargin;
      const inkindMonthlyProfitCostBase = inkindDailyProfitCost * 30;
      
      // Calculate yearly data
      const pdfYearlyData = seasonalData.map(({ month, mult, label }) => {
        const adjNetProfit = baseMonthlyNetProfit * mult;
        const loanPmt = results.loan.monthlyPayment;
        const loanPct = (loanPmt / adjNetProfit) * 100;
        const inkindCost = inkindMonthlyProfitCostBase * mult;
        const inkindPct = (inkindCost / adjNetProfit) * 100;
        return { month, label, adjNetProfit, loanPmt, loanPct, inkindCost, inkindPct, 
                 profitAfterLoan: adjNetProfit - loanPmt, profitAfterInkind: adjNetProfit - inkindCost };
      });
      
      const pdfYearlyTotals = pdfYearlyData.reduce((acc, m) => ({
        netProfit: acc.netProfit + m.adjNetProfit,
        loanPmts: acc.loanPmts + m.loanPmt,
        inkindCost: acc.inkindCost + m.inkindCost,
        profitAfterLoan: acc.profitAfterLoan + m.profitAfterLoan,
        profitAfterInkind: acc.profitAfterInkind + m.profitAfterInkind,
      }), { netProfit: 0, loanPmts: 0, inkindCost: 0, profitAfterLoan: 0, profitAfterInkind: 0 });
      
      // Bank Loan Monthly Table
      y = addSubTitle('Bank Loan: Fixed Payment vs. Seasonal Net Profit', y);
      addText('Payment stays the same regardless of how busy or slow you are - hits harder in slow months!', margin, y, { fontSize: 8, color: [100, 100, 100] });
      y += 6;
      
      // Table header
      pdf.setFillColor(255, 240, 240);
      pdf.rect(margin, y, contentWidth, 7, 'F');
      addText('Month', margin + 2, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText('Net Profit', margin + 30, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText('Loan Payment', margin + 65, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText('% of Profit', margin + 105, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText('Remaining', margin + 145, y + 5, { fontSize: 7, fontStyle: 'bold' });
      y += 8;
      
      // Table rows
      pdfYearlyData.forEach((data) => {
        addText(data.month + ' (' + data.label + ')', margin + 2, y + 4, { fontSize: 7 });
        addText(formatCurrency(data.adjNetProfit), margin + 30, y + 4, { fontSize: 7 });
        addText(formatCurrency(data.loanPmt), margin + 65, y + 4, { fontSize: 7, color: [180, 80, 80] });
        addText(data.loanPct.toFixed(1) + '%', margin + 105, y + 4, { fontSize: 7, fontStyle: 'bold', color: data.loanPct > 30 ? [200, 50, 50] : [100, 100, 100] });
        addText(formatCurrency(data.profitAfterLoan), margin + 145, y + 4, { fontSize: 7, color: [0, 120, 0] });
        y += 5;
      });
      
      // Yearly total row
      pdf.setFillColor(255, 230, 230);
      pdf.rect(margin, y, contentWidth, 7, 'F');
      addText('YEARLY', margin + 2, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText(formatCurrency(pdfYearlyTotals.netProfit), margin + 30, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText(formatCurrency(pdfYearlyTotals.loanPmts), margin + 65, y + 5, { fontSize: 7, fontStyle: 'bold', color: [180, 80, 80] });
      addText(((pdfYearlyTotals.loanPmts / pdfYearlyTotals.netProfit) * 100).toFixed(1) + '%', margin + 105, y + 5, { fontSize: 7, fontStyle: 'bold', color: [180, 80, 80] });
      addText(formatCurrency(pdfYearlyTotals.profitAfterLoan), margin + 145, y + 5, { fontSize: 7, fontStyle: 'bold', color: [0, 120, 0] });
      y += 12;
      
      // ===================== PAGE 6: INKIND SEASONAL TABLE =====================
      pdf.addPage();
      y = 20;
      
      y = addSectionTitle('INKIND: SCALES WITH YOUR BUSINESS', y);
      
      addText('inKind redemption scales proportionally with customer traffic - constant % of profit!', margin, y, { fontSize: 9 });
      y += 8;
      
      y = addSubTitle('inKind: Profit from ' + inkindCustomersPerDay.toFixed(1) + ' customers/day (scales with traffic)', y);
      addText('Only the net profit from customers redeeming credits - stays at ' + inkindPercentOfDailyProfit.toFixed(1) + '% regardless of season.', margin, y, { fontSize: 8, color: [100, 100, 100] });
      y += 6;
      
      // Table header
      pdf.setFillColor(255, 250, 235);
      pdf.rect(margin, y, contentWidth, 7, 'F');
      addText('Month', margin + 2, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText('Net Profit', margin + 30, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText('inKind Cost', margin + 65, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText('% of Profit', margin + 105, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText('Remaining', margin + 145, y + 5, { fontSize: 7, fontStyle: 'bold' });
      y += 8;
      
      // Table rows
      pdfYearlyData.forEach((data) => {
        addText(data.month + ' (' + data.label + ')', margin + 2, y + 4, { fontSize: 7 });
        addText(formatCurrency(data.adjNetProfit), margin + 30, y + 4, { fontSize: 7 });
        addText(formatCurrency(data.inkindCost), margin + 65, y + 4, { fontSize: 7, color: [150, 120, 60] });
        addText(data.inkindPct.toFixed(1) + '%', margin + 105, y + 4, { fontSize: 7, fontStyle: 'bold', color: [150, 120, 60] });
        addText(formatCurrency(data.profitAfterInkind), margin + 145, y + 4, { fontSize: 7, color: [0, 120, 0] });
        y += 5;
      });
      
      // Yearly total row
      pdf.setFillColor(255, 245, 220);
      pdf.rect(margin, y, contentWidth, 7, 'F');
      addText('YEARLY', margin + 2, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText(formatCurrency(pdfYearlyTotals.netProfit), margin + 30, y + 5, { fontSize: 7, fontStyle: 'bold' });
      addText(formatCurrency(pdfYearlyTotals.inkindCost), margin + 65, y + 5, { fontSize: 7, fontStyle: 'bold', color: [150, 120, 60] });
      addText(((pdfYearlyTotals.inkindCost / pdfYearlyTotals.netProfit) * 100).toFixed(1) + '%', margin + 105, y + 5, { fontSize: 7, fontStyle: 'bold', color: [150, 120, 60] });
      addText(formatCurrency(pdfYearlyTotals.profitAfterInkind), margin + 145, y + 5, { fontSize: 7, fontStyle: 'bold', color: [0, 120, 0] });
      y += 15;
      
      // Annual Profit Comparison Box
      y = addSubTitle('Annual Profit Remaining After Financing', y);
      
      const thirdWidth = (contentWidth - 10) / 3;
      
      // Bank Loan result
      pdf.setDrawColor(180, 80, 80);
      pdf.setLineWidth(1);
      pdf.rect(margin, y, thirdWidth, 28, 'S');
      addText('With Bank Loan', margin + thirdWidth/2, y + 6, { fontSize: 8, color: [100, 100, 100], align: 'center' });
      addText(formatCurrency(pdfYearlyTotals.profitAfterLoan), margin + thirdWidth/2, y + 15, { fontSize: 12, fontStyle: 'bold', align: 'center' });
      addText(((pdfYearlyTotals.loanPmts / pdfYearlyTotals.netProfit) * 100).toFixed(1) + '% to loan', margin + thirdWidth/2, y + 23, { fontSize: 7, color: [180, 80, 80], align: 'center' });
      
      // inKind result
      pdf.setDrawColor(0, 150, 0);
      pdf.setFillColor(240, 255, 240);
      pdf.rect(margin + thirdWidth + 5, y, thirdWidth, 28, 'FD');
      addText('With inKind', margin + thirdWidth + 5 + thirdWidth/2, y + 6, { fontSize: 8, color: [100, 100, 100], align: 'center' });
      addText(formatCurrency(pdfYearlyTotals.profitAfterInkind), margin + thirdWidth + 5 + thirdWidth/2, y + 15, { fontSize: 12, fontStyle: 'bold', color: [0, 120, 0], align: 'center' });
      addText(((pdfYearlyTotals.inkindCost / pdfYearlyTotals.netProfit) * 100).toFixed(1) + '% to inKind', margin + thirdWidth + 5 + thirdWidth/2, y + 23, { fontSize: 7, color: [150, 120, 60], align: 'center' });
      
      // Extra profit
      pdf.setDrawColor(197, 165, 114);
      pdf.setFillColor(255, 250, 235);
      pdf.rect(margin + (thirdWidth + 5) * 2, y, thirdWidth, 28, 'FD');
      addText('Extra with inKind', margin + (thirdWidth + 5) * 2 + thirdWidth/2, y + 6, { fontSize: 8, color: [100, 100, 100], align: 'center' });
      addText('+' + formatCurrency(pdfYearlyTotals.profitAfterInkind - pdfYearlyTotals.profitAfterLoan), margin + (thirdWidth + 5) * 2 + thirdWidth/2, y + 15, { fontSize: 12, fontStyle: 'bold', color: [150, 120, 60], align: 'center' });
      addText('per year', margin + (thirdWidth + 5) * 2 + thirdWidth/2, y + 23, { fontSize: 7, color: [100, 100, 100], align: 'center' });
      y += 35;
      
      // Key Insight Box - Operational Stress
      pdf.setFillColor(255, 250, 235);
      pdf.setDrawColor(197, 165, 114);
      pdf.setLineWidth(1.5);
      pdf.rect(margin, y, contentWidth, 35, 'FD');
      addText('KEY INSIGHT: LOWER OPERATIONAL STRESS', margin + 5, y + 7, { fontSize: 9, fontStyle: 'bold', color: [150, 120, 60] });
      const janData = pdfYearlyData[0];
      addText(`In your slowest month (Jan), the bank loan takes ${janData.loanPct.toFixed(1)}% of your net profit`, margin + 5, y + 14, { fontSize: 8 });
      addText(`vs inKind's ${janData.inkindPct.toFixed(1)}%. That's a ${(janData.loanPct - janData.inkindPct).toFixed(1)} percentage point difference when you can least afford it!`, margin + 5, y + 20, { fontSize: 8 });
      addText('Less debt pressure = less stress on you and your staff, lower operational burden, and more', margin + 5, y + 27, { fontSize: 7, color: [100, 100, 100] });
      addText('breathing room to focus on what matters - serving great food and building your business.', margin + 5, y + 32, { fontSize: 7, color: [100, 100, 100] });
      y += 42;
      
      // ===================== PAGE 7: DISCLAIMERS =====================
      pdf.addPage();
      y = 20;
      
      y = addSectionTitle('IMPORTANT DISCLAIMERS', y);
      
      y = addSubTitle('About This Report', y);
      y = addBulletPoint('This report is for educational and comparison purposes only', y);
      y = addBulletPoint('All calculations are estimates based on your inputs', y);
      y = addBulletPoint('Actual results may vary based on your specific circumstances', y);
      y += 5;
      
      y = addSubTitle('Assumptions in This Report', y);
      y = addBulletPoint(`Net Profit Margin: ${formatPercentDecimal(netMargin)}`, y);
      y = addBulletPoint(`Interest Rate: ${formatPercentWhole(loanInterestRate)} APR`, y);
      y = addBulletPoint(`Customer volume: ${avgCustomersPerDay} customers/day`, y);
      y = addBulletPoint(`Average ticket: ${formatCurrency(avgRevenuePerCustomer)}/customer`, y);
      y += 5;
      
      y = addSubTitle('Legal Disclaimer', y);
      pdf.setDrawColor(200, 200, 200);
      pdf.rect(margin, y, contentWidth, 24, 'S');
      addText('This calculator and website are NOT affiliated with, endorsed by, or connected to', margin + 3, y + 6, { fontSize: 8 });
      addText('inKind or inKind Capital. This is an independent educational tool.', margin + 3, y + 12, { fontSize: 8 });
      addText('Consult qualified financial and legal professionals before making decisions.', margin + 3, y + 18, { fontSize: 8 });
      
      pdf.save('inkind-savings-analysis.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const chartData = results ? [
    {
      name: 'inKind',
      'Monthly Cost': parseFloat(results.inkind.amountPerMonth.toFixed(2)),
      'Customers Needed/Day': parseFloat(results.inkind.customersPerDay.toFixed(2)),
    },
    {
      name: 'Traditional Loan',
      'Monthly Cost': parseFloat(results.loan.monthlyPayment.toFixed(2)),
      'Customers Needed/Day': parseFloat(results.loan.customersNeededPerDay.toFixed(2)),
    },
  ] : [];

  // Calculate additional inKind credit for same COSTS
  const totalLoanCost = results ? results.loan.totalPaidBack : 0;
  const additionalInkindForSameCost = results && totalLoanCost > 0 ? (totalLoanCost / (loanAmount * 2)) * loanAmount : 0;
  const percentageMore = results && loanAmount > 0 ? ((additionalInkindForSameCost / loanAmount - 1) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      <Navigation />
      <Hero />
      
      {/* Calculator Section */}
      <section id="calculator" className="py-16 px-4 bg-gradient-to-b from-[#0f0f10] to-[#1a1a1c]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Calculate Your Savings
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-6">
              See how much your restaurant can save with inKind compared to traditional bank financing
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button 
                onClick={loadQuickService}
                className="bg-[#C5A572]/20 hover:bg-[#C5A572]/30 text-[#C5A572] border border-[#C5A572]/50 font-semibold"
              >
                Quick Service Example
              </Button>
              <Button 
                onClick={loadCasualDining}
                className="bg-[#C5A572] hover:bg-[#B89562] text-black font-semibold"
              >
                Casual Dining Example
              </Button>
              <Button 
                onClick={loadFineDining}
                className="bg-[#C5A572]/20 hover:bg-[#C5A572]/30 text-[#C5A572] border border-[#C5A572]/50 font-semibold"
              >
                Fine Dining Example
              </Button>
            </div>
            <p className="text-gray-500 text-sm mt-3">
              Click an example to pre-fill realistic values, then adjust to match your restaurant
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {/* Financing Details */}
            <Card className="bg-[#1a1a1c] border-[#C5A572]/30">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-[#C5A572] flex items-center gap-2 uppercase tracking-wide">
                  <Calculator className="w-5 h-5" />
                  Financing Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Row 1: Loan Amount, Term, Interest Rate */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Loan Amount</Label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-black text-sm">$</span>
                      <Input
                        type="number"
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(e.target.value)}
                        placeholder="xxxxxxxx"
                        className="bg-[#C5A572] border-[#A8895D] text-black pl-6 h-9 text-sm placeholder:text-black/40"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Term (Months)</Label>
                    <Input
                      type="number"
                      value={loanTermMonths}
                      onChange={(e) => setLoanTermMonths(e.target.value)}
                      placeholder="xx"
                      className="bg-[#C5A572] border-[#A8895D] text-black h-9 text-sm placeholder:text-black/40"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Interest Rate</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.1"
                        value={loanInterestRate}
                        onChange={(e) => setLoanInterestRate(e.target.value)}
                        placeholder="xx"
                        className="bg-[#C5A572] border-[#A8895D] text-black pr-6 h-9 text-sm placeholder:text-black/40"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-black text-sm">%</span>
                    </div>
                  </div>
                </div>

                {/* inKind Section */}
                <div className="pt-4 mt-4 border-t border-[#C5A572]/20">
                  <h4 className="text-[#C5A572] font-semibold text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    inKind Details
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-gray-300 text-sm">Pre-Purchased F&B Investment from inKind (same as loan amount)</Label>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                        <Input
                          type="number"
                          value={loanAmount}
                          disabled
                          className="bg-gray-700/50 border-gray-600 text-gray-400 cursor-not-allowed pl-6 h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-gray-300 text-sm">Payback Term (Months)</Label>
                      <p className="text-gray-500 text-xs mt-1">24 months typical for $0-100k • 36 months typical for $100k-200k • $200k+ varies</p>
                      <Input
                        type="number"
                        value={inkindPaybackMonths}
                        onChange={(e) => setInkindPaybackMonths(e.target.value)}
                        placeholder="xx"
                        className="bg-[#C5A572] border-[#A8895D] text-black h-9 text-sm placeholder:text-black/40"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer & Revenue Info */}
            <Card className="bg-[#1a1a1c] border-[#C5A572]/30">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-[#C5A572] flex items-center gap-2 uppercase tracking-wide">
                  <Users className="w-5 h-5" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Avg Customers Per Day <span className="text-red-400">*</span></Label>
                    <Input
                      type="number"
                      value={avgCustomersPerDay}
                      onChange={(e) => setAvgCustomersPerDay(e.target.value)}
                      placeholder="xxx"
                      className="bg-[#C5A572] border-[#A8895D] text-black h-9 text-sm placeholder:text-black/40"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Avg Revenue Per Customer <span className="text-red-400">*</span></Label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-black text-sm">$</span>
                      <Input
                        type="number"
                        value={avgRevenuePerCustomer}
                        onChange={(e) => setAvgRevenuePerCustomer(e.target.value)}
                        placeholder="xx"
                        className="bg-[#C5A572] border-[#A8895D] text-black pl-6 h-9 text-sm placeholder:text-black/40"
                      />
                    </div>
                  </div>
                </div>
                {avgCustomersPerDay && avgRevenuePerCustomer && (
                  <div className="p-3 bg-white/[0.02] rounded-lg border border-gray-800/50">
                    <p className="text-gray-400 text-sm">
                      Estimated Monthly Revenue: <span className="text-white font-semibold">${formatMoney(Number(avgCustomersPerDay) * Number(avgRevenuePerCustomer) * 30)}</span>
                      <span className="text-gray-500 text-xs ml-2">({avgCustomersPerDay} customers × ${avgRevenuePerCustomer} × 30 days)</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Profit Margin */}
            <Card className="bg-[#1a1a1c] border-[#C5A572]/30">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-[#C5A572] flex items-center gap-2 uppercase tracking-wide">
                  <DollarSign className="w-5 h-5" />
                  Profit Margin
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Margin Input Type Toggle */}
                <div className="flex gap-2 p-1 bg-black/30 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setMarginInputType("direct")}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      marginInputType === "direct"
                        ? "bg-[#C5A572] text-black"
                        : "text-gray-400 hover:text-white"
                    }`}
                  >
                    Enter Net Margin %
                  </button>
                  <button
                    type="button"
                    onClick={() => setMarginInputType("detailed")}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      marginInputType === "detailed"
                        ? "bg-[#C5A572] text-black"
                        : "text-gray-400 hover:text-white"
                    }`}
                  >
                    Enter Cost Breakdown
                  </button>
                </div>

                {marginInputType === "direct" ? (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-gray-300 text-sm">Net Profit Margin <span className="text-red-400">*</span></Label>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.5"
                          value={directNetMargin}
                          onChange={(e) => setDirectNetMargin(e.target.value)}
                          placeholder="e.g., 7"
                          className="bg-[#C5A572] border-[#A8895D] text-black pr-6 h-9 text-sm placeholder:text-black/40"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-black text-sm">%</span>
                      </div>
                    </div>
                    <div className="p-3 bg-white/[0.02] rounded-lg border border-gray-800/50">
                      <p className="text-gray-400 text-xs">
                        <strong className="text-[#C5A572]">Typical Net Margins:</strong> QSR: 7-9% • Casual Dining: 5-7% • Fine Dining: 3-5%
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* COGS */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-gray-300 text-sm">Cost of Goods Sold</Label>
                        <Select value={cogsType} onValueChange={setCogsType}>
                          <SelectTrigger className="bg-[#C5A572] border-[#A8895D] text-black h-9 text-sm">
                            <SelectValue placeholder="Select one..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Percentage of Revenue">% of Revenue</SelectItem>
                            <SelectItem value="Flat Amount">Flat Amount/mo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {cogsType && (
                        <div className="space-y-1.5">
                          <Label className="text-gray-300 text-sm">{cogsType === "Percentage of Revenue" ? "COGS %" : "COGS Amount/mo"}</Label>
                          <div className="relative">
                            {cogsType === "Flat Amount" && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-black text-sm">$</span>}
                            <Input
                              type="number"
                              step={cogsType === "Percentage of Revenue" ? "1" : "100"}
                              value={cogsType === "Percentage of Revenue" ? cogsPercentage : cogsAmount}
                              onChange={(e) => cogsType === "Percentage of Revenue" ? setCogsPercentage(e.target.value) : setCogsAmount(e.target.value)}
                              placeholder={cogsType === "Percentage of Revenue" ? "xx" : "xxxxxxxx"}
                              className={`bg-[#C5A572] border-[#A8895D] text-black h-9 text-sm placeholder:text-black/40 ${cogsType === "Flat Amount" ? "pl-6" : "pr-6"}`}
                            />
                            {cogsType === "Percentage of Revenue" && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-black text-sm">%</span>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Labor */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-gray-300 text-sm">Labor Costs</Label>
                        <Select value={laborType} onValueChange={setLaborType}>
                          <SelectTrigger className="bg-[#C5A572] border-[#A8895D] text-black h-9 text-sm">
                            <SelectValue placeholder="Select one..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Percentage of Revenue">% of Revenue</SelectItem>
                            <SelectItem value="Flat Amount">Flat Amount/mo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {laborType && (
                        <div className="space-y-1.5">
                          <Label className="text-gray-300 text-sm">{laborType === "Percentage of Revenue" ? "Labor %" : "Labor Amount/mo"}</Label>
                          <div className="relative">
                            {laborType === "Flat Amount" && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-black text-sm">$</span>}
                            <Input
                              type="number"
                              step={laborType === "Percentage of Revenue" ? "1" : "100"}
                              value={laborType === "Percentage of Revenue" ? laborPercentage : laborAmount}
                              onChange={(e) => laborType === "Percentage of Revenue" ? setLaborPercentage(e.target.value) : setLaborAmount(e.target.value)}
                              placeholder={laborType === "Percentage of Revenue" ? "xx" : "xxxxxxxx"}
                              className={`bg-[#C5A572] border-[#A8895D] text-black h-9 text-sm placeholder:text-black/40 ${laborType === "Flat Amount" ? "pl-6" : "pr-6"}`}
                            />
                            {laborType === "Percentage of Revenue" && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-black text-sm">%</span>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Overhead */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-gray-300 text-sm">Overhead & Operating Costs</Label>
                        <Select value={overheadType} onValueChange={setOverheadType}>
                          <SelectTrigger className="bg-[#C5A572] border-[#A8895D] text-black h-9 text-sm">
                            <SelectValue placeholder="Select one..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Percentage of Revenue">% of Revenue</SelectItem>
                            <SelectItem value="Flat Amount">Flat Amount/mo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {overheadType && (
                        <div className="space-y-1.5">
                          <Label className="text-gray-300 text-sm">{overheadType === "Percentage of Revenue" ? "Overhead %" : "Overhead Amount/mo"}</Label>
                          <div className="relative">
                            {overheadType === "Flat Amount" && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-black text-sm">$</span>}
                            <Input
                              type="number"
                              step={overheadType === "Percentage of Revenue" ? "1" : "100"}
                              value={overheadType === "Percentage of Revenue" ? overheadPercentage : overheadAmount}
                              onChange={(e) => overheadType === "Percentage of Revenue" ? setOverheadPercentage(e.target.value) : setOverheadAmount(e.target.value)}
                              placeholder={overheadType === "Percentage of Revenue" ? "xx" : "xxxxxxxx"}
                              className={`bg-[#C5A572] border-[#A8895D] text-black h-9 text-sm placeholder:text-black/40 ${overheadType === "Flat Amount" ? "pl-6" : "pr-6"}`}
                            />
                            {overheadType === "Percentage of Revenue" && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-black text-sm">%</span>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Tax Rate - needed to calculate net margin from gross */}
                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-700/50">
                      <div className="space-y-1.5">
                        <Label className="text-gray-300 text-sm">Est. Federal Tax Rate <span className="text-red-400">*</span></Label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="1"
                            value={federalTaxRate}
                            onChange={(e) => setFederalTaxRate(e.target.value)}
                            placeholder="xx"
                            className="bg-[#C5A572] border-[#A8895D] text-black pr-6 h-9 text-sm placeholder:text-black/40"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-black text-sm">%</span>
                        </div>
                      </div>
                      <div className="flex items-end pb-1">
                        <p className="text-gray-500 text-xs">
                          Used to calculate your net margin (what you keep after taxes)
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Negative Margin Error */}
            {negativeMarginError && errorDetails && (
              <Card className="bg-red-950/50 border-red-500/50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-400 text-lg">!</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-red-400 font-semibold mb-2">Negative Profit Margin</h4>
                      <p className="text-gray-300 text-sm mb-4">
                        Your costs exceed your revenue. You cannot make a profit with these numbers.
                      </p>
                      <div className="bg-black/30 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Monthly Revenue:</span>
                          <span className="text-white font-mono">${formatMoney(errorDetails.monthlyRevenue)}</span>
                        </div>
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">COGS:</span>
                            <span className="text-red-400 font-mono">-${formatMoney(errorDetails.cogsCost)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Labor:</span>
                            <span className="text-red-400 font-mono">-${formatMoney(errorDetails.laborCost)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Overhead:</span>
                            <span className="text-red-400 font-mono">-${formatMoney(errorDetails.overheadCost)}</span>
                          </div>
                        </div>
                        <div className="border-t border-gray-700 pt-2">
                          <div className="flex justify-between text-sm font-semibold">
                            <span className="text-gray-300">Total Costs:</span>
                            <span className="text-red-400 font-mono">${formatMoney(errorDetails.totalCosts)}</span>
                          </div>
                        </div>
                        <div className="border-t border-red-700 pt-2">
                          <div className="flex justify-between text-sm font-bold">
                            <span className="text-red-400">Deficit (Loss):</span>
                            <span className="text-red-400 font-mono">-${formatMoney(errorDetails.deficit)}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-400 text-xs mt-3">
                        Reduce your costs or increase customer count/revenue per customer to achieve a positive margin.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Calculate Button */}
            <div className="flex justify-center pt-4">
              <Button 
                onClick={handleCalculate}
                disabled={!canCalculate()}
                className={`px-8 py-3 text-lg font-semibold ${
                  canCalculate() 
                    ? 'bg-[#C5A572] hover:bg-[#B89562] text-black' 
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Calculator className="w-5 h-5 mr-2" />
                Calculate Savings
              </Button>
            </div>
            {!canCalculate() && (
              <p className="text-center text-gray-500 text-sm">
                Fill in all required fields to calculate your savings
              </p>
            )}
          </div>

          {/* Results Section - Now Below Inputs */}
          {results && hasCalculated && (
            <div className="max-w-5xl mx-auto space-y-6 mt-12">
              <div className="text-center mb-8">
                <h3 className="text-4xl font-bold text-white mb-2">
                  Your Savings Calculation
                </h3>
                <p className="text-gray-400">
                  Based on the information you provided
                </p>
              </div>

              {/* Calculate extra months savings */}
              {(() => {
                const extraMonths = Number(loanTermMonths) - Number(inkindPaybackMonths);
                const monthlyLoanPayment = results.loan.monthlyPayment;
                const extraMonthsSavings = extraMonths > 0 ? extraMonths * monthlyLoanPayment : 0;
                const totalLoanCost = results.loan.monthlyPayment * Number(loanTermMonths);
                
                // Calculate total combined savings (no tax):
                // Retained profit only applies during inKind payback period
                const savingsOverInkindTerm = results.inkind.additionalMonthlyNetProfit * Number(inkindPaybackMonths);
                // Extra months = loan payments you DON'T make after inKind is paid off
                const totalCombinedSavings = savingsOverInkindTerm + extraMonthsSavings;

                // Calculate margins and costs
                const monthlyRevenue = Number(avgCustomersPerDay) * Number(avgRevenuePerCustomer) * 30;
                const netMargin = results.restaurant.netMargin;
                
                const revenuePerCustomer = Number(avgRevenuePerCustomer);
                
                // NET profit per customer
                const netProfitPerCustomer = revenuePerCustomer * netMargin;

                // Calculate total customers needed to cover entire financing using NET profit
                // For bank loan: need NET PROFIT from customers to pay back total loan
                const totalCustomersForLoan = totalLoanCost / netProfitPerCustomer;
                
                // For inKind: customers spend at menu price, that amount comes off the balance
                // So you need REVENUE equal to the credit amount ($200k)
                const inkindCredit = Number(loanAmount) * 2;
                const totalCustomersForInkind = inkindCredit / revenuePerCustomer;

                const chartData = [
                  { name: "inKind", "Customers Needed/Day": results.inkind.customersPerDay },
                  { name: "Traditional Loan", "Customers Needed/Day": results.loan.customersNeededPerDay }
                ];

                return (
                  <>
                    {/* Gold/White Savings Summary */}
                    <Card className="bg-gradient-to-br from-[#1a1a1c] to-[#252528] border border-[#C5A572]/30">
                      <CardContent className="pt-8 pb-8">
                        <div className="text-center mb-8">
                          <h3 className="text-3xl md:text-4xl font-bold text-[#C5A572] uppercase tracking-wide">Your Additional Profit with inKind</h3>
                          <p className="text-base text-gray-400 mt-2">Compared to a traditional bank loan</p>
                          </div>
                          
                          {/* Clean Numbers */}
                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-[#C5A572]/5 rounded-xl p-8 text-center border border-[#C5A572]/20">
                              <p className="text-lg text-[#C5A572] uppercase tracking-widest mb-4 font-bold">Monthly</p>
                              <p className="text-5xl md:text-6xl font-bold text-white tracking-tight">
                                ${formatMoney(results.inkind.additionalMonthlyNetProfit)}
                              </p>
                              <p className="text-gray-500 text-sm mt-4">profit retained</p>
                            </div>
                            
                            <div className="bg-[#C5A572]/10 rounded-xl p-8 text-center border border-[#C5A572]/30">
                              <p className="text-lg text-[#C5A572] uppercase tracking-widest mb-4 font-bold">Yearly</p>
                              <p className="text-5xl md:text-6xl font-bold text-white tracking-tight">
                                ${formatMoney(results.inkind.additionalYearlyNetProfit)}
                              </p>
                              <p className="text-gray-500 text-sm mt-4">profit retained</p>
                            </div>
                          </div>

                          {/* Extra Months Bonus */}
                          {extraMonths > 0 && (
                            <div className="mt-8 p-6 bg-[#C5A572]/10 rounded-xl border border-[#C5A572]/30">
                              <div className="text-center">
                                <p className="text-[#C5A572] text-xl md:text-2xl font-bold uppercase tracking-wide mb-3">
                                  + {extraMonths} Extra Months of Savings
                                </p>
                                <p className="text-5xl md:text-6xl font-bold text-white">
                                  ${formatMoney(extraMonthsSavings)}
                                </p>
                                <p className="text-gray-500 text-sm mt-4">
                                  {extraMonths} months × ${formatMoney(monthlyLoanPayment)}/mo loan payment you won't be making
                                </p>
                              </div>
                            </div>
                          )}

                          {/* TOTAL COMBINED SAVINGS */}
                          <div className="mt-8 p-8 bg-gradient-to-r from-[#C5A572]/20 to-[#C5A572]/10 rounded-xl border-2 border-[#C5A572]">
                            <div className="text-center">
                              <p className="text-[#C5A572] text-xl md:text-2xl font-bold uppercase tracking-widest mb-4">
                                Total Combined Savings
                              </p>
                              <p className="text-6xl md:text-7xl font-bold text-white tracking-tight">
                                ${formatMoney(totalCombinedSavings)}
                              </p>
                              <p className="text-gray-400 text-base mt-4">
                                Over the full {loanTermMonths}-month loan term
                              </p>
                              <div className="mt-6 pt-6 border-t border-[#C5A572]/30 text-base text-gray-500">
                                <p>${formatMoney(savingsOverInkindTerm)} from retained profit ({inkindPaybackMonths} months)</p>
                                {extraMonths > 0 && (
                                  <p className="mt-2">+ ${formatMoney(extraMonthsSavings)} from {extraMonths} months of avoided loan payments</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Prominent Download PDF Section */}
                      <div className="bg-gradient-to-r from-[#C5A572] to-[#B89562] p-6 rounded-xl shadow-lg">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                          <div className="text-center md:text-left">
                            <h3 className="text-xl md:text-2xl font-bold text-black">Get Your Comprehensive Analysis</h3>
                            <p className="text-black/70 text-sm md:text-base">Download a detailed, printer-friendly PDF with step-by-step calculations</p>
                          </div>
                          <Button
                            onClick={downloadPDF}
                            data-testid="download-pdf-main-btn"
                            className="bg-black hover:bg-gray-800 text-white font-bold text-lg md:text-xl px-8 py-6 h-auto flex items-center gap-3 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                          >
                            <Download className="w-6 h-6 md:w-7 md:h-7" />
                            Download Full Report
                          </Button>
                        </div>
                      </div>

                      {/* Savings Breakdown - Detailed Steps */}
                      <div className="p-8 bg-[#1a1a1c] rounded-xl border border-[#C5A572]/30">
                        <div className="flex justify-between items-center mb-6">
                          <h3 className="text-2xl md:text-3xl font-bold text-[#C5A572] uppercase tracking-wide">Savings Breakdown</h3>
                          <Button
                            onClick={downloadPDF}
                            data-testid="download-pdf-secondary-btn"
                            className="bg-[#C5A572] hover:bg-[#B89562] text-black font-semibold flex items-center gap-2 px-4 py-2"
                          >
                            <Download className="w-5 h-5" />
                            PDF
                          </Button>
                        </div>
                        <div ref={breakdownRef}>
                          <BreakdownSection 
                            results={results} 
                            loanAmount={loanAmount}
                            loanTermMonths={loanTermMonths}
                            inkindPaybackMonths={inkindPaybackMonths}
                          />
                        </div>
                      </div>

                      {/* What You Pay vs. What You Get + Customer Breakdown */}
                      <Card className="bg-[#1a1a1c] border-[#C5A572]/30">
                        <CardContent className="pt-6">
                          <div className="text-center mb-6">
                            <h3 className="text-xl font-bold text-[#C5A572] uppercase tracking-wide">What You Pay vs. What You Get</h3>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-4 mb-8">
                            {/* Bank Loan */}
                            <div className="p-5 rounded-xl bg-red-950/30 border border-red-900/30">
                              <p className="text-red-400/80 text-xs uppercase tracking-wider mb-4">Traditional Bank Loan</p>
                              <div className="space-y-3">
                                <div className="flex justify-between items-baseline">
                                  <span className="text-gray-500 text-sm">You receive</span>
                                  <span className="text-white font-mono">${formatMoney(Number(loanAmount))}</span>
                                </div>
                                <div className="flex justify-between items-baseline">
                                  <span className="text-gray-500 text-sm">You pay back</span>
                                  <span className="text-white font-mono">${formatMoney(totalLoanCost)}</span>
                                </div>
                                <div className="flex justify-between items-baseline pt-3 border-t border-red-900/30">
                                  <span className="text-red-400/80 text-sm">Cost of financing</span>
                                  <span className="text-red-400 font-mono font-semibold">${formatMoney(totalLoanCost - Number(loanAmount))}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* inKind */}
                            <div className="p-5 rounded-xl bg-[#C5A572]/5 border border-[#C5A572]/20">
                              <p className="text-[#C5A572] text-xs uppercase tracking-wider mb-4">inKind</p>
                              <div className="space-y-3">
                                <div className="flex justify-between items-baseline">
                                  <span className="text-gray-500 text-sm">You receive</span>
                                  <span className="text-white font-mono">${formatMoney(Number(loanAmount))}</span>
                                </div>
                                <div className="flex justify-between items-baseline">
                                  <span className="text-gray-500 text-sm">Credit value (2×)</span>
                                  <span className="text-white font-mono">${formatMoney(Number(loanAmount) * 2)}</span>
                                </div>
                                <div className="flex justify-between items-baseline pt-3 border-t border-[#C5A572]/20">
                                  <span className="text-[#C5A572] text-sm">Cost of financing</span>
                                  <span className="text-[#C5A572] font-mono font-semibold">$0</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Customer Breakdown - How many customers cover financing */}
                          <div className="border-t border-gray-800 pt-8">
                            <h4 className="text-2xl md:text-3xl font-bold text-white text-center mb-4">How Many Customers Cover Your Financing?</h4>
                            <p className="text-gray-400 text-base text-center mb-6">
                              Using net profit per customer: <span className="text-white font-semibold">${formatMoney(netProfitPerCustomer)}</span> ({(netMargin * 100).toFixed(1)}% of ${formatMoney(revenuePerCustomer)})
                            </p>
                            
                            <div className="grid md:grid-cols-2 gap-6">
                              {/* Bank Loan Customers */}
                              <div className="p-6 rounded-xl bg-red-950/20 border border-red-900/30">
                                <p className="text-red-400/80 text-sm uppercase tracking-wider mb-4 font-semibold">Bank Loan</p>
                                <p className="text-4xl md:text-5xl font-bold text-white mb-3">{results.loan.customersNeededPerDay.toFixed(1)} <span className="text-xl font-normal text-gray-500">customers/day</span></p>
                                <div className="text-sm text-gray-500 space-y-2 mt-4 pt-4 border-t border-red-900/30">
                                  <p>Monthly payment: ${formatMoney(results.loan.monthlyPayment)}</p>
                                  <p>÷ 30 days = ${formatMoney(results.loan.monthlyPayment / 30)}/day needed</p>
                                  <p>÷ ${formatMoney(netProfitPerCustomer)} net profit/customer</p>
                                  <p>= <span className="text-red-400 font-semibold">{results.loan.customersNeededPerDay.toFixed(1)} customers</span></p>
                                </div>
                              </div>

                              {/* inKind Customers */}
                              <div className="p-6 rounded-xl bg-[#C5A572]/5 border border-[#C5A572]/30">
                                <p className="text-[#C5A572] text-sm uppercase tracking-wider mb-4 font-semibold">inKind</p>
                                <p className="text-4xl md:text-5xl font-bold text-white mb-3">{results.inkind.customersPerDay.toFixed(1)} <span className="text-xl font-normal text-gray-500">customers/day</span></p>
                                <div className="text-sm text-gray-500 space-y-2 mt-4 pt-4 border-t border-[#C5A572]/30">
                                  <p>Monthly redemption: ${formatMoney(results.inkind.amountPerMonth)}</p>
                                  <p>÷ 30 days = ${formatMoney(results.inkind.amountPerMonth / 30)}/day needed</p>
                                  <p>÷ ${formatMoney(revenuePerCustomer)} revenue/customer</p>
                                  <p>= <span className="text-[#C5A572] font-semibold">{results.inkind.customersPerDay.toFixed(1)} customers</span></p>
                                </div>
                              </div>
                            </div>

                            {/* BIGGER Daily Savings Callout */}
                            <div className="mt-8 p-8 bg-gradient-to-r from-[#C5A572]/30 to-[#C5A572]/10 rounded-xl border-2 border-[#C5A572] text-center">
                              <p className="text-[#C5A572] text-3xl md:text-4xl font-bold">
                                You save {(results.loan.customersNeededPerDay - results.inkind.customersPerDay).toFixed(1)} customers' worth of profit
                              </p>
                              <p className="text-white text-4xl md:text-5xl font-bold mt-3">
                                EVERY SINGLE DAY!
                              </p>
                              <p className="text-gray-400 text-base mt-4">
                                {(results.loan.customersNeededPerDay - results.inkind.customersPerDay).toFixed(1)} customers × ${formatMoney(netProfitPerCustomer)} net profit = <span className="text-[#C5A572] font-semibold text-lg">${formatMoney((results.loan.customersNeededPerDay - results.inkind.customersPerDay) * netProfitPerCustomer)}/day</span> in retained profit
                              </p>
                            </div>
                          </div>


                          {/* NEW: Total Customers to Pay Off Entire Financing */}
                          <div className="border-t border-gray-800 pt-8 mt-8">
                            <h4 className="text-2xl md:text-3xl font-bold text-white text-center mb-4">Total Customers to Pay Off Your Entire Financing</h4>
                            <p className="text-gray-400 text-base text-center mb-4">
                              At ${formatMoney(revenuePerCustomer)} per customer | {(netMargin * 100).toFixed(1)}% net margin
                            </p>
                            <p className="text-gray-500 text-sm text-center mb-8">
                              Net profit per customer: <span className="text-white font-semibold">${formatMoney(netProfitPerCustomer)}</span>
                            </p>
                            
                            <div className="grid md:grid-cols-2 gap-6">
                              {/* Bank Loan Total Customers */}
                              <div className="p-6 rounded-xl bg-red-950/30 border border-red-900/30">
                                <p className="text-red-400/80 text-base uppercase tracking-wider mb-4 font-semibold">Bank Loan</p>
                                <p className="text-5xl md:text-6xl font-bold text-white mb-3">{Math.ceil(totalCustomersForLoan).toLocaleString()}</p>
                                <p className="text-gray-500 text-base">total customers needed</p>
                                <div className="text-sm text-gray-500 space-y-2 mt-4 pt-4 border-t border-red-900/30">
                                  <p>Total to pay back: ${formatMoney(totalLoanCost)}</p>
                                  <p>÷ ${formatMoney(netProfitPerCustomer)} net profit/customer</p>
                                  <p>= <span className="text-red-400 font-semibold">{Math.ceil(totalCustomersForLoan).toLocaleString()} customers</span></p>
                                </div>
                              </div>

                              {/* inKind Total Customers */}
                              <div className="p-6 rounded-xl bg-[#C5A572]/5 border border-[#C5A572]/30">
                                <p className="text-[#C5A572] text-base uppercase tracking-wider mb-4 font-semibold">inKind</p>
                                <p className="text-5xl md:text-6xl font-bold text-white mb-3">{Math.ceil(totalCustomersForInkind).toLocaleString()}</p>
                                <p className="text-gray-500 text-base">total customers needed</p>
                                <div className="text-sm text-gray-500 space-y-2 mt-4 pt-4 border-t border-[#C5A572]/30">
                                  <p>Credit to redeem: ${formatMoney(inkindCredit)}</p>
                                  <p>÷ ${formatMoney(revenuePerCustomer)} revenue/customer</p>
                                  <p>= <span className="text-[#C5A572] font-semibold">{Math.ceil(totalCustomersForInkind).toLocaleString()} customers</span></p>
                                </div>
                              </div>
                            </div>

                            <div className="mt-8 p-6 bg-gradient-to-r from-[#C5A572]/20 to-[#C5A572]/10 rounded-xl border-2 border-[#C5A572] text-center">
                              <p className="text-[#C5A572] text-2xl md:text-3xl font-bold">
                                inKind needs {Math.abs(Math.ceil(totalCustomersForInkind) - Math.ceil(totalCustomersForLoan)).toLocaleString()} {totalCustomersForInkind < totalCustomersForLoan ? 'fewer' : 'more'} customers
                              </p>
                              <p className="text-gray-400 text-base mt-4">
                                to pay off your financing completely
                              </p>
                            </div>
                          </div>

                          {/* Daily Profit Commitment Section */}
                          <div className="border-t border-gray-800 pt-8 mt-8">
                            <h4 className="text-2xl md:text-3xl font-bold text-white text-center mb-4">Daily Operational Burden</h4>
                            <p className="text-gray-400 text-base text-center mb-4">
                              How much of your daily net profit goes toward financing
                            </p>
                            <p className="text-[#C5A572] text-sm text-center mb-8 max-w-2xl mx-auto">
                              Fewer customers needed to service debt = less operational pressure, less stress on staff, and more sustainable operations
                            </p>
                            
                            {(() => {
                              const dailyNetProfit = Number(avgCustomersPerDay) * revenuePerCustomer * netMargin;
                              const dailyLoanCost = results.loan.monthlyPayment / 30;
                              const loanPercentOfProfit = (dailyLoanCost / dailyNetProfit) * 100;
                              
                              // inKind: only the net profit from customers needed for redemption
                              const inkindCustomersNeeded = results.inkind.customersPerDay;
                              const inkindProfitCost = inkindCustomersNeeded * netProfitPerCustomer;
                              const inkindPercentOfProfit = (inkindProfitCost / dailyNetProfit) * 100;
                              
                              // Calculate customer difference for operational burden
                              const customerDiff = results.loan.customersNeededPerDay - inkindCustomersNeeded;
                              
                              return (
                                <>
                                  <div className="grid md:grid-cols-2 gap-6">
                                    <div className="p-6 rounded-xl bg-red-950/30 border border-red-900/30 text-center">
                                      <p className="text-red-400/80 text-base uppercase tracking-wider mb-4 font-semibold">Bank Loan</p>
                                      <p className="text-5xl md:text-6xl font-bold text-white mb-2">
                                        {loanPercentOfProfit.toFixed(1)}%
                                      </p>
                                      <p className="text-gray-500 text-sm">of daily net profit</p>
                                      <div className="mt-4 text-sm text-gray-400 space-y-1">
                                        <p>Daily loan payment: <span className="text-red-400">${formatMoney(dailyLoanCost)}</span></p>
                                        <p>Daily net profit: <span className="text-white">${formatMoney(dailyNetProfit)}</span></p>
                                      </div>
                                      <div className="mt-4 pt-4 border-t border-red-900/30">
                                        <p className="text-red-400 text-sm font-semibold">
                                          {results.loan.customersNeededPerDay.toFixed(1)} customers/day just for debt
                                        </p>
                                      </div>
                                    </div>
                                    
                                    <div className="p-6 rounded-xl bg-[#C5A572]/5 border border-[#C5A572]/30 text-center">
                                      <p className="text-[#C5A572] text-base uppercase tracking-wider mb-4 font-semibold">inKind</p>
                                      <p className="text-5xl md:text-6xl font-bold text-white mb-2">
                                        {inkindPercentOfProfit.toFixed(1)}%
                                      </p>
                                      <p className="text-gray-500 text-sm">of daily net profit</p>
                                      <div className="mt-4 text-sm text-gray-400 space-y-1">
                                        <p>Profit from {inkindCustomersNeeded.toFixed(1)} customers: <span className="text-[#C5A572]">${formatMoney(inkindProfitCost)}</span></p>
                                        <p>({inkindCustomersNeeded.toFixed(1)} × ${formatMoney(netProfitPerCustomer)} net profit/customer)</p>
                                      </div>
                                      <div className="mt-4 pt-4 border-t border-[#C5A572]/30">
                                        <p className="text-[#C5A572] text-sm font-semibold">
                                          Only {inkindCustomersNeeded.toFixed(1)} customers/day for financing
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Operational Burden Highlight */}
                                  <div className="mt-6 p-6 bg-gradient-to-r from-green-950/30 to-[#C5A572]/10 rounded-xl border border-green-900/30">
                                    <h5 className="text-lg font-bold text-white text-center mb-4">
                                      Reduced Operational Pressure with inKind
                                    </h5>
                                    <div className="grid md:grid-cols-3 gap-4 text-center">
                                      <div>
                                        <p className="text-3xl font-bold text-[#C5A572]">{customerDiff.toFixed(1)}</p>
                                        <p className="text-gray-400 text-sm">fewer customers/day to service debt</p>
                                      </div>
                                      <div>
                                        <p className="text-3xl font-bold text-[#C5A572]">{(customerDiff * 30).toFixed(0)}</p>
                                        <p className="text-gray-400 text-sm">fewer customers/month</p>
                                      </div>
                                      <div>
                                        <p className="text-3xl font-bold text-[#C5A572]">{(customerDiff * 365).toFixed(0)}</p>
                                        <p className="text-gray-400 text-sm">fewer customers/year</p>
                                      </div>
                                    </div>
                                    <p className="text-gray-500 text-sm text-center mt-4">
                                      Less pressure on your kitchen, staff, and operations. More time for quality service.
                                    </p>
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          {/* Seasonal Business Cycle Visualization */}
                          {(() => {
                            // Seasonal multipliers for typical restaurant (1.0 = average month)
                            const seasonalMultipliers = [
                              { month: 'Jan', multiplier: 0.75, label: 'Slow' },
                              { month: 'Feb', multiplier: 0.80, label: 'Slow' },
                              { month: 'Mar', multiplier: 0.95, label: 'Average' },
                              { month: 'Apr', multiplier: 1.05, label: 'Average' },
                              { month: 'May', multiplier: 1.15, label: 'Busy' },
                              { month: 'Jun', multiplier: 1.20, label: 'Busy' },
                              { month: 'Jul', multiplier: 1.25, label: 'Peak' },
                              { month: 'Aug', multiplier: 1.20, label: 'Busy' },
                              { month: 'Sep', multiplier: 1.00, label: 'Average' },
                              { month: 'Oct', multiplier: 1.05, label: 'Average' },
                              { month: 'Nov', multiplier: 1.10, label: 'Busy' },
                              { month: 'Dec', multiplier: 1.30, label: 'Peak' },
                            ];
                            
                            const baseMonthlyRevenue = monthlyRevenue;
                            const monthlyLoanPayment = results.loan.monthlyPayment;
                            const baseMonthlyNetProfit = baseMonthlyRevenue * netMargin;
                            
                            // inKind: calculate the net profit "cost" from customers needed for redemption
                            const inkindCustomersPerDay = results.inkind.customersPerDay;
                            const inkindDailyProfitCost = inkindCustomersPerDay * netProfitPerCustomer;
                            const inkindMonthlyProfitCost = inkindDailyProfitCost * 30;
                            
                            // Calculate data for each month
                            const yearlyData = seasonalMultipliers.map(({ month, multiplier, label }) => {
                              const adjustedRevenue = baseMonthlyRevenue * multiplier;
                              const adjustedCustomers = Number(avgCustomersPerDay) * 30 * multiplier;
                              const adjustedNetProfit = adjustedRevenue * netMargin;
                              
                              // Bank loan payment stays FIXED - show as % of NET PROFIT
                              const loanPayment = monthlyLoanPayment;
                              const loanPercentOfProfit = (loanPayment / adjustedNetProfit) * 100;
                              
                              // inKind: profit from customers needed scales with traffic
                              // More customers = proportionally more inKind customers redeem
                              const inkindProfitCost = inkindMonthlyProfitCost * multiplier;
                              const inkindPercentOfProfit = (inkindProfitCost / adjustedNetProfit) * 100;
                              
                              // Net profit REMAINING after financing
                              const profitAfterLoan = adjustedNetProfit - loanPayment;
                              const profitAfterInkind = adjustedNetProfit - inkindProfitCost;
                              
                              return {
                                month,
                                label,
                                multiplier,
                                revenue: adjustedRevenue,
                                netProfit: adjustedNetProfit,
                                customers: adjustedCustomers,
                                loanPayment,
                                loanPercentOfProfit,
                                inkindProfitCost,
                                inkindPercentOfProfit,
                                profitAfterLoan,
                                profitAfterInkind,
                              };
                            });
                            
                            // Calculate yearly totals
                            const yearlyTotals = yearlyData.reduce((acc, month) => ({
                              revenue: acc.revenue + month.revenue,
                              netProfit: acc.netProfit + month.netProfit,
                              loanPayments: acc.loanPayments + month.loanPayment,
                              inkindProfitCost: acc.inkindProfitCost + month.inkindProfitCost,
                              profitAfterLoan: acc.profitAfterLoan + month.profitAfterLoan,
                              profitAfterInkind: acc.profitAfterInkind + month.profitAfterInkind,
                            }), { revenue: 0, netProfit: 0, loanPayments: 0, inkindProfitCost: 0, profitAfterLoan: 0, profitAfterInkind: 0 });
                            
                            return (
                              <div className="border-t border-gray-800 pt-8 mt-8">
                                <h4 className="text-2xl md:text-3xl font-bold text-white text-center mb-4">
                                  12-Month Business Cycle Comparison
                                </h4>
                                <p className="text-gray-400 text-base text-center mb-8">
                                  See how each financing option eats into your NET PROFIT through seasonal ups and downs
                                </p>
                                
                                {/* Bank Loan Chart */}
                                <div className="mb-12">
                                  <h5 className="text-xl font-bold text-red-400 mb-4 flex items-center gap-2">
                                    <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                                    Bank Loan: Fixed ${formatMoney(monthlyLoanPayment)}/month from your NET PROFIT
                                  </h5>
                                  <p className="text-gray-500 text-sm mb-4">
                                    Payment stays the same regardless of how busy or slow you are - hits harder in slow months
                                  </p>
                                  
                                  <div className="h-80 bg-black/30 rounded-xl p-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <BarChart data={yearlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis dataKey="month" stroke="#888" />
                                        <YAxis stroke="#888" tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} />
                                        <Tooltip 
                                          contentStyle={{ backgroundColor: '#1a1a1c', border: '1px solid #333' }}
                                          formatter={(value, name) => {
                                            if (name === 'Net Profit') return [`$${formatMoney(value)}`, 'Monthly Net Profit'];
                                            if (name === 'Loan Payment') return [`$${formatMoney(value)}`, 'Loan Payment (Fixed)'];
                                            return [value, name];
                                          }}
                                          labelFormatter={(label) => {
                                            const data = yearlyData.find(d => d.month === label);
                                            return `${label} (${data?.label}) - ${(data?.multiplier * 100).toFixed(0)}% of average`;
                                          }}
                                        />
                                        <Bar dataKey="netProfit" name="Net Profit" fill="#4a5568" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="loanPayment" name="Loan Payment" fill="#ef4444" radius={[4, 4, 0, 0]} />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>
                                  
                                  {/* Monthly breakdown table for Bank Loan */}
                                  <div className="mt-4 overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-gray-700">
                                          <th className="text-left py-2 px-2 text-gray-400">Month</th>
                                          <th className="text-right py-2 px-2 text-gray-400">Net Profit</th>
                                          <th className="text-right py-2 px-2 text-gray-400">Loan Payment</th>
                                          <th className="text-right py-2 px-2 text-gray-400">% of Profit</th>
                                          <th className="text-right py-2 px-2 text-gray-400">Profit Remaining</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {yearlyData.map((data) => (
                                          <tr key={data.month} className="border-b border-gray-800/50">
                                            <td className="py-2 px-2 text-white">{data.month} <span className="text-gray-500 text-xs">({data.label})</span></td>
                                            <td className="py-2 px-2 text-right text-white">${formatMoney(data.netProfit)}</td>
                                            <td className="py-2 px-2 text-right text-red-400">${formatMoney(data.loanPayment)}</td>
                                            <td className="py-2 px-2 text-right">
                                              <span className={`font-semibold ${data.loanPercentOfProfit > 30 ? 'text-red-400' : data.loanPercentOfProfit > 20 ? 'text-yellow-400' : 'text-green-400'}`}>
                                                {data.loanPercentOfProfit.toFixed(1)}%
                                              </span>
                                            </td>
                                            <td className={`py-2 px-2 text-right font-semibold ${data.profitAfterLoan >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                              ${formatMoney(data.profitAfterLoan)}
                                            </td>
                                          </tr>
                                        ))}
                                        <tr className="bg-red-950/30 font-bold">
                                          <td className="py-3 px-2 text-white">YEARLY TOTAL</td>
                                          <td className="py-3 px-2 text-right text-white">${formatMoney(yearlyTotals.netProfit)}</td>
                                          <td className="py-3 px-2 text-right text-red-400">${formatMoney(yearlyTotals.loanPayments)}</td>
                                          <td className="py-3 px-2 text-right text-red-400 font-semibold">
                                            {((yearlyTotals.loanPayments / yearlyTotals.netProfit) * 100).toFixed(1)}%
                                          </td>
                                          <td className={`py-3 px-2 text-right font-semibold ${yearlyTotals.profitAfterLoan >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            ${formatMoney(yearlyTotals.profitAfterLoan)}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                
                                {/* inKind Chart */}
                                <div className="mb-8">
                                  <h5 className="text-xl font-bold text-[#C5A572] mb-4 flex items-center gap-2">
                                    <span className="w-3 h-3 bg-[#C5A572] rounded-full"></span>
                                    inKind: Profit from {inkindCustomersPerDay.toFixed(1)} customers/day (scales with traffic)
                                  </h5>
                                  <p className="text-gray-500 text-sm mb-4">
                                    Only the net profit from customers redeeming credits - {inkindCustomersPerDay.toFixed(1)} customers × ${formatMoney(netProfitPerCustomer)} = ${formatMoney(inkindDailyProfitCost)}/day base
                                  </p>
                                  
                                  <div className="h-80 bg-black/30 rounded-xl p-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <BarChart data={yearlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                        <XAxis dataKey="month" stroke="#888" />
                                        <YAxis stroke="#888" tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} />
                                        <Tooltip 
                                          contentStyle={{ backgroundColor: '#1a1a1c', border: '1px solid #333' }}
                                          formatter={(value, name) => {
                                            if (name === 'Net Profit') return [`$${formatMoney(value)}`, 'Monthly Net Profit'];
                                            if (name === 'inKind Profit Cost') return [`$${formatMoney(value)}`, 'Profit from inKind Customers'];
                                            return [value, name];
                                          }}
                                          labelFormatter={(label) => {
                                            const data = yearlyData.find(d => d.month === label);
                                            return `${label} (${data?.label}) - ${(data?.multiplier * 100).toFixed(0)}% of average`;
                                          }}
                                        />
                                        <Bar dataKey="netProfit" name="Net Profit" fill="#4a5568" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="inkindProfitCost" name="inKind Profit Cost" fill="#C5A572" radius={[4, 4, 0, 0]} />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>
                                  
                                  {/* Monthly breakdown table for inKind */}
                                  <div className="mt-4 overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-gray-700">
                                          <th className="text-left py-2 px-2 text-gray-400">Month</th>
                                          <th className="text-right py-2 px-2 text-gray-400">Net Profit</th>
                                          <th className="text-right py-2 px-2 text-gray-400">inKind Customer Profit</th>
                                          <th className="text-right py-2 px-2 text-gray-400">% of Profit</th>
                                          <th className="text-right py-2 px-2 text-gray-400">Profit Remaining</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {yearlyData.map((data) => (
                                          <tr key={data.month} className="border-b border-gray-800/50">
                                            <td className="py-2 px-2 text-white">{data.month} <span className="text-gray-500 text-xs">({data.label})</span></td>
                                            <td className="py-2 px-2 text-right text-white">${formatMoney(data.netProfit)}</td>
                                            <td className="py-2 px-2 text-right text-[#C5A572]">${formatMoney(data.inkindProfitCost)}</td>
                                            <td className="py-2 px-2 text-right text-[#C5A572] font-semibold">
                                              {data.inkindPercentOfProfit.toFixed(1)}%
                                            </td>
                                            <td className="py-2 px-2 text-right text-green-400 font-semibold">
                                              ${formatMoney(data.profitAfterInkind)}
                                            </td>
                                          </tr>
                                        ))}
                                        <tr className="bg-[#C5A572]/10 font-bold">
                                          <td className="py-3 px-2 text-white">YEARLY TOTAL</td>
                                          <td className="py-3 px-2 text-right text-white">${formatMoney(yearlyTotals.netProfit)}</td>
                                          <td className="py-3 px-2 text-right text-[#C5A572]">${formatMoney(yearlyTotals.inkindProfitCost)}</td>
                                          <td className="py-3 px-2 text-right text-[#C5A572] font-semibold">
                                            {((yearlyTotals.inkindProfitCost / yearlyTotals.netProfit) * 100).toFixed(1)}%
                                          </td>
                                          <td className="py-3 px-2 text-right text-green-400 font-semibold">
                                            ${formatMoney(yearlyTotals.profitAfterInkind)}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                
                                {/* Net Profit Comparison Summary */}
                                <div className="mt-8 p-6 bg-gradient-to-r from-green-950/30 to-[#C5A572]/10 rounded-xl border border-green-900/30">
                                  <h5 className="text-xl font-bold text-white text-center mb-6">
                                    Annual Profit Remaining After Financing
                                  </h5>
                                  <div className="grid md:grid-cols-3 gap-6 text-center">
                                    <div>
                                      <p className="text-gray-400 text-sm mb-2">With Bank Loan</p>
                                      <p className={`text-3xl font-bold ${yearlyTotals.profitAfterLoan >= 0 ? 'text-white' : 'text-red-400'}`}>
                                        ${formatMoney(yearlyTotals.profitAfterLoan)}
                                      </p>
                                      <p className="text-red-400 text-sm mt-1">
                                        ({((yearlyTotals.loanPayments / yearlyTotals.netProfit) * 100).toFixed(1)}% of profit to loan)
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-gray-400 text-sm mb-2">With inKind</p>
                                      <p className="text-3xl font-bold text-green-400">
                                        ${formatMoney(yearlyTotals.profitAfterInkind)}
                                      </p>
                                      <p className="text-[#C5A572] text-sm mt-1">
                                        ({((yearlyTotals.inkindProfitCost / yearlyTotals.netProfit) * 100).toFixed(1)}% of profit to inKind customers)
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-gray-400 text-sm mb-2">Extra Profit with inKind</p>
                                      <p className="text-3xl font-bold text-[#C5A572]">
                                        +${formatMoney(yearlyTotals.profitAfterInkind - yearlyTotals.profitAfterLoan)}
                                      </p>
                                      <p className="text-gray-500 text-sm mt-1">
                                        per year
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Key Insight */}
                                <div className="mt-6 p-4 bg-[#C5A572]/10 rounded-lg border border-[#C5A572]/30">
                                  <p className="text-[#C5A572] font-semibold mb-2">Key Insight: Lower Operational Stress</p>
                                  <p className="text-gray-400 text-sm mb-3">
                                    In your slowest month (Jan), the bank loan takes <span className="text-red-400 font-semibold">{yearlyData[0].loanPercentOfProfit.toFixed(1)}%</span> of your net profit vs inKind's <span className="text-[#C5A572] font-semibold">{yearlyData[0].inkindPercentOfProfit.toFixed(1)}%</span>. 
                                    That's a <span className="text-white font-semibold">{(yearlyData[0].loanPercentOfProfit - yearlyData[0].inkindPercentOfProfit).toFixed(1)} percentage point difference</span> when you can least afford it!
                                  </p>
                                  <p className="text-gray-500 text-sm">
                                    <span className="text-[#C5A572]">Less debt pressure</span> means less stress on you and your staff, 
                                    lower operational burden, and more breathing room to focus on what matters - serving great food and building your business.
                                  </p>
                                </div>
                              </div>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    </>
                  );
                })()}
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-4 bg-gradient-to-b from-[#1a1a1c] to-[#0f0f10]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Why Choose inKind?
            </h2>
            <p className="text-gray-400 text-xl">
              More than just financing - a complete growth partner
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Stress Free & Quicker!",
                description: "Usually paid off 2+ years sooner than a commercial restaurant loan",
                icon: <Clock className="w-8 h-8" />
              },
              {
                title: "No Set Payments in Slow Months!",
                description: "Flexibility when you need it most - payments scale with your redemptions",
                icon: <TrendingUp className="w-8 h-8" />
              },
              {
                title: "No Personal Guarantee or Collateral!",
                description: "Protect your personal assets - no worries if something happens",
                icon: <CheckCircle2 className="w-8 h-8" />
              },
              {
                title: "Non-Restrictive, Non-Dilutive, Interest Free!",
                description: "Keep full control of your business with zero interest charges",
                icon: <DollarSign className="w-8 h-8" />
              },
              {
                title: "We Market Your Restaurant!",
                description: "Access to 4M+ diners and growing - bringing customers to your door",
                icon: <Users className="w-8 h-8" />
              },
              {
                title: "Fast Funding!",
                description: "Average of 3-5 business days after signing to access capital",
                icon: <Sparkles className="w-8 h-8" />
              },
              {
                title: "No Credit Checks!",
                description: "Approval based on your restaurant's performance, not credit score",
                icon: <CheckCircle2 className="w-8 h-8" />
              },
              {
                title: "Flexible Redemption Options!",
                description: "Customers redeem credits naturally through dining - no forced payment schedules impacting your cash flow",
                icon: <Calculator className="w-8 h-8" />
              }
            ].map((benefit, index) => (
              <Card key={index} className="bg-gradient-to-br from-[#2a2a2c] to-[#1a1a1c] border-[#D4A574]/20 hover:border-[#D4A574]/50 transition-all duration-300 hover:scale-105">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-[#D4A574]/10 rounded-lg text-[#D4A574]">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                      <p className="text-gray-400">{benefit.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-8 px-4">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <p className="text-gray-400 text-sm">
            <strong>Disclaimer:</strong> This calculator and website are not affiliated with, endorsed by, or connected to inKind or inKind Capital. 
            This is an independent tool for educational and comparison purposes only.
          </p>
          <p className="text-gray-500 text-xs">
            This tool does not constitute tax, legal, or financial advice. Results are estimates only and may vary based on your specific circumstances. 
            Please consult with qualified professionals before making any financial decisions.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CalculatorPage;
